import React, { Suspense, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  InputAdornment,
  LinearProgress,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import BuildCircleRoundedIcon from '@mui/icons-material/BuildCircleRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import AddCircleRoundedIcon from '@mui/icons-material/AddCircleRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import EngineeringRoundedIcon from '@mui/icons-material/EngineeringRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';
import LayersRoundedIcon from '@mui/icons-material/LayersRounded';
import LocalPrintshopRoundedIcon from '@mui/icons-material/LocalPrintshopRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import MapRoundedIcon from '@mui/icons-material/MapRounded';
import NotificationsActiveRoundedIcon from '@mui/icons-material/NotificationsActiveRounded';
import PublishRoundedIcon from '@mui/icons-material/PublishRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import ShareRoundedIcon from '@mui/icons-material/ShareRounded';
import SettingsSuggestRoundedIcon from '@mui/icons-material/SettingsSuggestRounded';
import TimelineRoundedIcon from '@mui/icons-material/TimelineRounded';
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded';
import ViewInArRoundedIcon from '@mui/icons-material/ViewInArRounded';
import WorkRoundedIcon from '@mui/icons-material/WorkRounded';
import { CircleMarker, MapContainer, Popup, TileLayer } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import SchemaBodyFields from '../messages/SchemaBodyFields';
import { formatDateTime, getPartnerDisplayName } from '../messages/messageUtils';
import axiosInstance from '../../../utils/axiosInstance';
import ThreadFileRenderers from './ThreadFileRenderers';

const PartModelViewer = React.lazy(() => import('./PartModelViewer'));

const cardIconMap = {
  capabilities_assets_advertisements: <Inventory2RoundedIcon fontSize="small" />,
  capabilities_assets_offers: <Inventory2RoundedIcon fontSize="small" />,
  selected_capability: <CheckCircleRoundedIcon fontSize="small" />,
  selected_asset: <CheckCircleRoundedIcon fontSize="small" />,
  available_exchange_cards: <AddCircleRoundedIcon fontSize="small" />,
  exchange_card_initial_request: <InfoOutlinedIcon fontSize="small" />,
  exchange_card_attach_managed_asset: <Inventory2RoundedIcon fontSize="small" />,
  exchange_card_eddie_assistance: <EngineeringRoundedIcon fontSize="small" />,
  exchange_card_verify_printer_readiness: <FactCheckRoundedIcon fontSize="small" />,
  exchange_card_slicing_instructions: <SettingsSuggestRoundedIcon fontSize="small" />,
  exchange_card_sliced_file_upload: <UploadFileRoundedIcon fontSize="small" />,
  exchange_card_view_telemetry: <TimelineRoundedIcon fontSize="small" />,
  exchange_card_view_2d_drafting_format: <MapRoundedIcon fontSize="small" />,
  exchange_card_view_3d_solid_format: <ViewInArRoundedIcon fontSize="small" />,
  exchange_card_view_mesh_format: <LayersRoundedIcon fontSize="small" />,
  exchange_card_view_bim_architecture_format: <WorkRoundedIcon fontSize="small" />,
  exchange_card_view_gcode: <TimelineRoundedIcon fontSize="small" />,
  exchange_card_view_3d_scanned_model: <ViewInArRoundedIcon fontSize="small" />,
  exchange_card_printer_file_ready: <LocalPrintshopRoundedIcon fontSize="small" />,
  exchange_card_print_authorization: <FactCheckRoundedIcon fontSize="small" />,
  exchange_card_start_print_job: <SendRoundedIcon fontSize="small" />,
  exchange_card_print_job_started: <LocalPrintshopRoundedIcon fontSize="small" />,
  exchange_card_print_job_status_update: <TimelineRoundedIcon fontSize="small" />,
  exchange_card_print_job_completed: <CheckCircleRoundedIcon fontSize="small" />,
  exchange_card_print_job_error: <ErrorOutlineRoundedIcon fontSize="small" />,
  exchange_card_print_pickup_confirmation: <Inventory2RoundedIcon fontSize="small" />,
  exchange_card_xtdp: <ViewInArRoundedIcon fontSize="small" />,
  partner_confirmation: <CheckCircleRoundedIcon fontSize="small" />,
  provider_partner_notified: <NotificationsActiveRoundedIcon fontSize="small" />,
  machine_selection: <BuildCircleRoundedIcon fontSize="small" />,
  machine_telemetry: <TimelineRoundedIcon fontSize="small" />,
  expert_recommendations: <EngineeringRoundedIcon fontSize="small" />,
  part_model: <ViewInArRoundedIcon fontSize="small" />,
  work_order_updates: <WorkRoundedIcon fontSize="small" />,
  files_exchanged: <UploadFileRoundedIcon fontSize="small" />,
  file_upload_event: <UploadFileRoundedIcon fontSize="small" />,
  error_messages: <ErrorOutlineRoundedIcon fontSize="small" />,
};

const titleMap = {
  initial_request: 'Initial Request',
  capabilities_assets_advertisements: 'Capabilities And Assets Advertisements',
  capabilities_assets_offers: 'Capabilities And Assets Advertisements',
  selected_capability: 'Selected Capability',
  selected_asset: 'Selected Asset',
  available_exchange_cards: 'Available Exchange Cards',
  exchange_card_initial_request: 'Initial Request',
  exchange_card_attach_managed_asset: 'Attach Managed Asset',
  exchange_card_eddie_assistance: 'Get Assistance from EDDIE',
  exchange_card_verify_printer_readiness: 'Verify Printer Readiness',
  exchange_card_slicing_instructions: 'Slicing Instructions',
  exchange_card_sliced_file_upload: 'Sliced File Upload',
  exchange_card_view_telemetry: 'View Telemetry',
  exchange_card_view_2d_drafting_format: 'View 2D Drafting Formats',
  exchange_card_view_3d_solid_format: 'View 3D Solid Formats',
  exchange_card_view_mesh_format: 'View Mesh Formats',
  exchange_card_view_bim_architecture_format: 'View BIM/Architecture Formats',
  exchange_card_view_gcode: 'View GCode',
  exchange_card_view_3d_scanned_model: 'View 3D Scanned Model',
  exchange_card_printer_file_ready: 'Printer File Ready',
  exchange_card_print_authorization: 'Print Authorization',
  exchange_card_start_print_job: 'Start Print Job',
  exchange_card_print_job_started: 'Print Job Started',
  exchange_card_print_job_status_update: 'Print Job Status Update',
  exchange_card_print_job_completed: 'Print Job Completed',
  exchange_card_print_job_error: 'Print Job Error',
  exchange_card_print_pickup_confirmation: 'Print Pickup Confirmation',
  exchange_card_xtdp: 'xTDP',
  partner_confirmation: 'Partner Confirmation',
  provider_partner_notified: 'Provider Partner Notified',
  machine_selection: 'Machine Selection',
  machine_telemetry: 'Machine Telemetry',
  expert_recommendations: 'Expert Recommendations',
  part_model: 'Part Model',
  work_order_updates: 'Work Order Updates',
  files_exchanged: 'Files Exchanged',
  file_upload_event: 'File Uploaded',
  error_messages: 'Error Messages',
};

const hiddenOfferFields = [
  'capability_source_id',
  'asset_source_id',
  'selected_offer_message_uuid',
  'offering_partner_id',
  'offering_partner',
];
const EXCHANGE_CARD_DRAG_TYPE = 'application/x-amx-exchange-card';

const getDisplayName = (item, fallback) => (
  item?.title ||
  item?.body?.capability_name ||
  item?.body?.asset_name ||
  item?.body?.name ||
  fallback
);

const getPartnerSubline = (partner = {}) => (
  [partner.legal_name, partner.partner_code, partner.contact_email]
    .filter(Boolean)
    .join(' • ')
);

const formatCategoryLabel = (value = '') => (
  String(value || '')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (match) => match.toUpperCase())
);

const getActorLabel = (actor = {}) => {
  const source = String(actor.source || '').toLowerCase();
  if (source === 'amx' || source === 'system') {
    return 'Added by AMX';
  }
  if (source === 'gateway') {
    return 'Added by gateway';
  }
  if (source === 'asset') {
    return 'Added by asset';
  }
  const name = actor.display_name || actor.user?.user_full_name || actor.user?.user_email;
  const partnerName = actor.partner?.display_name || actor.partner?.legal_name;
  if (name && partnerName) {
    return `Added by ${name} at ${partnerName}`;
  }
  if (name) {
    return `Added by ${name}`;
  }
  if (partnerName) {
    return `Added by ${partnerName}`;
  }
  return '';
};

const getStatusLabel = (status = '') => {
  const value = String(status || '').replace(/[_-]+/g, ' ').trim();
  return value ? value.replace(/\b\w/g, (match) => match.toUpperCase()) : 'Unknown';
};

const MetaBlock = ({ label, value }) => {
  if (!value) {
    return null;
  }

  return (
    <Box
      sx={{
        minWidth: 0,
        p: 1.25,
        borderRadius: 1.5,
        border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.72)}`,
        bgcolor: (theme) => alpha(theme.palette.background.default, 0.42),
      }}
    >
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 800 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ mt: 0.35, fontWeight: 750, wordBreak: 'break-word' }}>
        {value}
      </Typography>
    </Box>
  );
};

const searchableText = (value) => {
  if (value === null || typeof value === 'undefined') {
    return '';
  }
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value).toLowerCase();
    } catch {
      return String(value).toLowerCase();
    }
  }
  return String(value).toLowerCase();
};

const itemMatchesQuery = ({ query, partner, capability, assets = [] }) => {
  if (!query) {
    return true;
  }
  return [
    partner,
    capability?.title,
    capability?.body,
    ...assets.map((asset) => asset?.title),
    ...assets.map((asset) => asset?.body),
  ].some((part) => searchableText(part).includes(query));
};

const hasCoordinates = (body = {}) => {
  const latitude = Number(body.latitude);
  const longitude = Number(body.longitude);
  return body.latitude !== null &&
    typeof body.latitude !== 'undefined' &&
    body.longitude !== null &&
    typeof body.longitude !== 'undefined' &&
    Number.isFinite(latitude) &&
    Number.isFinite(longitude);
};

const buildOfferMapPoints = (partners = []) => {
  const points = [];
  partners.forEach((partnerGroup) => {
    const partnerName = getPartnerDisplayName(partnerGroup.offering_partner);

    (partnerGroup.capabilities_assets_list || []).forEach((capability) => {
      const capabilityBody = capability.body || {};
      const capabilityTitle = getDisplayName(capability, 'Capability');

      if (hasCoordinates(capabilityBody)) {
        points.push({
          id: `capability-${capability.message_uuid}`,
          type: 'capability',
          title: capabilityTitle,
          partnerName,
          latitude: Number(capabilityBody.latitude),
          longitude: Number(capabilityBody.longitude),
        });
      }

      (capability.assets || []).forEach((asset) => {
        const assetBody = asset.body || {};
        if (!hasCoordinates(assetBody)) {
          return;
        }
        points.push({
          id: `asset-${asset.message_uuid}`,
          type: 'asset',
          title: getDisplayName(asset, 'Asset'),
          capabilityTitle,
          partnerName,
          latitude: Number(assetBody.latitude),
          longitude: Number(assetBody.longitude),
        });
      });
    });

    (partnerGroup.standalone_assets || []).forEach((asset) => {
      const assetBody = asset.body || {};
      if (!hasCoordinates(assetBody)) {
        return;
      }
      points.push({
        id: `standalone-asset-${asset.message_uuid}`,
        type: 'asset',
        title: getDisplayName(asset, 'Asset'),
        partnerName,
        latitude: Number(assetBody.latitude),
        longitude: Number(assetBody.longitude),
      });
    });
  });
  return points;
};

const OfferLocationMap = ({ points }) => {
  if (!points.length) {
    return (
      <Box
        sx={{
          minHeight: 220,
          borderRadius: 1.5,
          border: (theme) => `1px dashed ${alpha(theme.palette.divider, 0.9)}`,
          bgcolor: (theme) => alpha(theme.palette.background.default, 0.48),
          display: 'grid',
          placeItems: 'center',
          p: 2,
        }}
      >
        <Stack alignItems="center" spacing={0.75}>
          <MapRoundedIcon color="disabled" />
          <Typography variant="body2" color="text.secondary">
            No asset locations have been provided yet.
          </Typography>
        </Stack>
      </Box>
    );
  }

  const center = points.reduce(
    (acc, point) => [acc[0] + point.latitude / points.length, acc[1] + point.longitude / points.length],
    [0, 0],
  );

  return (
    <Box
      sx={{
        height: { xs: 260, md: 320 },
        borderRadius: 1.5,
        overflow: 'hidden',
        border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.84)}`,
        '& .leaflet-container': {
          height: '100%',
          width: '100%',
          fontFamily: 'inherit',
        },
      }}
    >
      <MapContainer center={center} zoom={points.length === 1 ? 10 : 5} scrollWheelZoom={false}>
        <TileLayer
          attribution="&copy; OpenStreetMap contributors"
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {points.map((point) => (
          <CircleMarker
            key={point.id}
            center={[point.latitude, point.longitude]}
            radius={point.type === 'capability' ? 9 : 7}
            pathOptions={{
              color: point.type === 'capability' ? '#0d6efd' : '#2e7d32',
              fillColor: point.type === 'capability' ? '#0d6efd' : '#2e7d32',
              fillOpacity: 0.72,
            }}
          >
            <Popup>
              <strong>{point.title}</strong>
              <br />
              {point.partnerName}
              {point.capabilityTitle ? (
                <>
                  <br />
                  {point.capabilityTitle}
                </>
              ) : null}
            </Popup>
          </CircleMarker>
        ))}
      </MapContainer>
    </Box>
  );
};

const TimelineStep = ({ cardKey, children }) => (
  <Box
    sx={{
      position: 'relative',
      pl: { xs: 4.5, md: 5 },
      pb: 2.25,
      '&:before': {
        content: '""',
        position: 'absolute',
        left: { xs: 17, md: 19 },
        top: 36,
        bottom: 0,
        width: 2,
        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.22),
      },
      '&:last-of-type:before': {
        display: 'none',
      },
    }}
  >
    <Box
      sx={{
        position: 'absolute',
        left: { xs: 3, md: 5 },
        top: 0,
        width: 30,
        height: 30,
        borderRadius: '50%',
        display: 'grid',
        placeItems: 'center',
        bgcolor: (theme) => theme.palette.background.paper,
        color: 'primary.main',
        border: (theme) => `2px solid ${theme.palette.primary.main}`,
        boxShadow: (theme) => `0 0 0 5px ${alpha(theme.palette.primary.main, 0.08)}`,
      }}
    >
      {cardIconMap[cardKey] || <Inventory2RoundedIcon fontSize="small" />}
    </Box>
    {children}
  </Box>
);

const CardShell = ({ cardKey, card, instruction, action = null, children, collapsible = false, expanded = true }) => (
  <Box
    sx={{
      borderRadius: 2,
      border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.82)}`,
      bgcolor: (theme) => alpha(theme.palette.background.paper, 0.94),
      overflow: 'hidden',
    }}
  >
    <Box
      sx={{
        p: { xs: 1.5, md: 2 },
        borderLeft: (theme) => `4px solid ${theme.palette.primary.main}`,
      }}
    >
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: 1.5,
              display: 'grid',
              placeItems: 'center',
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
              color: 'primary.main',
              flexShrink: 0,
            }}
          >
            {cardIconMap[cardKey] || <Inventory2RoundedIcon fontSize="small" />}
          </Box>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 850 }}>
              {card?.title || titleMap[cardKey] || cardKey}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatDateTime(card?.updated_at || card?.created_at)}
            </Typography>
            {getActorLabel(card?.added_by) ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.15 }}>
                {getActorLabel(card.added_by)}
              </Typography>
            ) : null}
          </Box>
        </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
          {action}
          {collapsible ? (
            <ExpandMoreRoundedIcon
              fontSize="small"
              sx={{
                transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 160ms ease',
              }}
            />
          ) : null}
        </Stack>
      </Stack>
    </Box>
    <Divider />
    <Box sx={{ p: { xs: 1.5, md: 2 } }}>
      {children}
    </Box>
  </Box>
);

const InitialRequest = ({ initialRequest, expanded = true }) => {
  const body = initialRequest?.request_body || {};
  if (!initialRequest?.msg_uuid && !Object.keys(body).length) {
    return null;
  }

  const instruction =
    initialRequest.instruction ||
    'Review the request details, then compare any partner capability advertisements that have arrived below.';

  return (
    <CardShell
      cardKey="initial_request"
      card={{ order: 0, created_at: initialRequest.created_at }}
      collapsible
      expanded={expanded}
    >
      <Collapse in={expanded} timeout="auto">
        {instruction ? (
          <Alert severity="info" sx={{ mb: 1.5 }}>
            {instruction}
          </Alert>
        ) : null}
        <Stack spacing={1.25}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(220px, 0.42fr)' }, gap: 1 }}>
            <MetaBlock
              label="Need Category"
              value={
                initialRequest.need_category_display_name ||
                formatCategoryLabel(initialRequest.need_category)
              }
            />
          </Box>
          <SchemaBodyFields body={body} compact />
        </Stack>
      </Collapse>
    </CardShell>
  );
};

const CapabilityOffer = ({ capability, actionsAllowed, selecting, onSelectCapability, onSelectAsset }) => {
  const assets = capability.assets || [];
  return (
    <Box
      sx={{
        borderRadius: 1.5,
        border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.72)}`,
        p: 1.5,
      }}
    >
      <Stack spacing={1.25}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} justifyContent="space-between">
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <SettingsSuggestRoundedIcon color="primary" fontSize="small" />
              <Typography variant="subtitle2" sx={{ fontWeight: 850, wordBreak: 'break-word' }}>
                {getDisplayName(capability, 'Capability')}
              </Typography>
            </Stack>
          </Box>
          {actionsAllowed ? (
            <Button
              size="small"
              variant="outlined"
              startIcon={<SendRoundedIcon />}
              disabled={selecting}
              onClick={() => onSelectCapability?.(capability)}
            >
              Request Capability
            </Button>
          ) : null}
        </Stack>
        <SchemaBodyFields body={capability.body || {}} hiddenFields={hiddenOfferFields} compact />
        {assets.length ? (
          <Stack spacing={1}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
              Assets
            </Typography>
            {assets.map((asset) => (
              <Box
                key={asset.message_uuid}
                sx={{
                  borderRadius: 1.5,
                  bgcolor: (theme) => alpha(theme.palette.success.main, 0.045),
                  border: (theme) => `1px solid ${alpha(theme.palette.success.main, 0.18)}`,
                  p: 1.25,
                }}
              >
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} justifyContent="space-between">
                  <Box sx={{ minWidth: 0 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <LayersRoundedIcon color="success" fontSize="small" />
                      <Typography variant="body2" sx={{ fontWeight: 800, wordBreak: 'break-word' }}>
                        {getDisplayName(asset, 'Asset')}
                      </Typography>
                    </Stack>
                  </Box>
                  {actionsAllowed ? (
                    <Button
                      size="small"
                      variant="outlined"
                      color="success"
                      disabled={selecting}
                      onClick={() => onSelectAsset?.(asset)}
                    >
                      Request Asset
                    </Button>
                  ) : null}
                </Stack>
                <Box sx={{ mt: 1 }}>
                  <SchemaBodyFields body={asset.body || {}} hiddenFields={hiddenOfferFields} compact />
                </Box>
              </Box>
            ))}
          </Stack>
        ) : null}
      </Stack>
    </Box>
  );
};

const OffersCard = ({ card, selecting, onSelectCapability, onSelectAsset }) => {
  const [query, setQuery] = useState('');
  const normalizedQuery = query.trim().toLowerCase();
  const visiblePartners = useMemo(() => (
    (card.partners || []).map((partnerGroup) => {
      const visibleCapabilities = (partnerGroup.capabilities_assets_list || []).filter((capability) => (
        itemMatchesQuery({
          query: normalizedQuery,
          partner: partnerGroup.offering_partner,
          capability,
          assets: capability.assets || [],
        })
      ));
      const visibleStandaloneAssets = (partnerGroup.standalone_assets || []).filter((asset) => (
        itemMatchesQuery({
          query: normalizedQuery,
          partner: partnerGroup.offering_partner,
          capability: asset,
          assets: [],
        })
      ));
      return {
        ...partnerGroup,
        capabilities_assets_list: visibleCapabilities,
        standalone_assets: visibleStandaloneAssets,
      };
    }).filter((partnerGroup) => (
      partnerGroup.capabilities_assets_list.length || partnerGroup.standalone_assets.length
    ))
  ), [card.partners, normalizedQuery]);
  const mapPoints = useMemo(() => buildOfferMapPoints(visiblePartners), [visiblePartners]);

  return (
    <Stack spacing={1.5}>
      {!card.actions_allowed ? (
        <Alert severity={card.selection_made ? 'success' : 'info'}>
          {card.selection_made
            ? 'A selection has already been made. You can review previous advertisements, but no additional selections are available for this thread.'
            : 'The advertisement selection window is closed. Review the advertisements below for context.'}
        </Alert>
      ) : null}
      <TextField
        size="small"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        label="Search capabilities or assets"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchRoundedIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />
      <OfferLocationMap points={mapPoints} />
      {visiblePartners.map((partnerGroup) => {
        const partner = partnerGroup.offering_partner || {};
        const partnerName = getPartnerDisplayName(partner);
        const subline = getPartnerSubline(partner);

        return (
          <Box
            key={partner.partner_id || partnerGroup.time_sent}
            sx={{
              borderRadius: 1.5,
              border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.78)}`,
              overflow: 'hidden',
            }}
          >
            <Box sx={{ p: 1.5, bgcolor: (theme) => alpha(theme.palette.info.main, 0.04) }}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} justifyContent="space-between">
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 900, wordBreak: 'break-word' }}>
                    {partnerName}
                  </Typography>
                  {subline ? (
                    <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                      {subline}
                    </Typography>
                  ) : null}
                </Box>
                <MetaBlock label="Latest Advertisement" value={formatDateTime(partnerGroup.time_sent)} />
              </Stack>
            </Box>
            <Stack spacing={1.25} sx={{ p: 1.5 }}>
              {partnerGroup.capabilities_assets_list.map((capability) => (
                <CapabilityOffer
                  key={capability.message_uuid}
                  capability={capability}
                  actionsAllowed={card.actions_allowed}
                  selecting={selecting}
                  onSelectCapability={onSelectCapability}
                  onSelectAsset={onSelectAsset}
                />
              ))}
              {partnerGroup.standalone_assets.map((asset) => (
                <Box
                  key={asset.message_uuid}
                  sx={{
                    borderRadius: 1.5,
                    border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.72)}`,
                    p: 1.5,
                  }}
                >
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} justifyContent="space-between">
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 850 }}>
                        {getDisplayName(asset, 'Asset')}
                      </Typography>
                    </Box>
                    {card.actions_allowed ? (
                      <Button size="small" variant="outlined" color="success" onClick={() => onSelectAsset?.(asset)}>
                        Request Asset
                      </Button>
                    ) : null}
                  </Stack>
                  <Box sx={{ mt: 1 }}>
                    <SchemaBodyFields body={asset.body || {}} hiddenFields={hiddenOfferFields} compact />
                  </Box>
                </Box>
              ))}
            </Stack>
          </Box>
        );
      })}
      {!visiblePartners.length ? (
        <Typography variant="body2" color="text.secondary">
          No capabilities or assets match the current search.
        </Typography>
      ) : null}
    </Stack>
  );
};

const SelectionCard = ({ card }) => {
  const body = card?.selection_message?.body || {};
  return (
    <Stack spacing={1}>
      {card?.offering_partner ? (
        <MetaBlock label="Provider" value={getPartnerDisplayName(card.offering_partner)} />
      ) : null}
      <SchemaBodyFields body={body} hiddenFields={hiddenOfferFields} compact />
    </Stack>
  );
};

const tdpOptionLabel = (tdp = {}) => (
  [
    tdp.name || tdp.tdp_title || tdp.tdp_id,
    tdp.version ? `v${tdp.version}` : '',
    getStatusLabel(tdp.status),
    tdp.source === 'shared' ? 'Shared' : '',
  ].filter(Boolean).join(' / ')
);

const buildXTdpPayload = (tdp = {}, exchangeNotes = '') => ({
  status: 'pulled',
  tdp_ids: [tdp.tdp_id],
  body: {
    tdp_id: tdp.tdp_id,
    tdp_name: tdp.name || tdp.tdp_title || 'xTDP',
    tdp_status: tdp.status || 'draft',
    version: tdp.version || '',
    owner_partner_id: tdp.owner_partner_id || '',
    owner_partner_name: (
      tdp.owner_partner?.display_name ||
      tdp.owner_partner?.legal_name ||
      tdp.owner_partner?.partner_code ||
      ''
    ),
    source: tdp.source || 'owned',
    exchange_notes: exchangeNotes,
    attachment_confirmed: true,
  },
});

const XTdpAttachmentCard = ({ card }) => {
  const body = card.body || {};
  const tdpIds = Array.isArray(card.tdp_ids) ? card.tdp_ids : [];
  const primaryTdpId = body.tdp_id || tdpIds[0] || '';
  return (
    <Stack spacing={1.5}>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 1 }}>
        <MetaBlock label="xTDP" value={body.tdp_name || primaryTdpId} />
        <MetaBlock label="Status" value={getStatusLabel(body.tdp_status)} />
        <MetaBlock label="Version" value={body.version || 'Unversioned'} />
        <MetaBlock label="Owner" value={body.owner_partner_name || body.owner_partner_id} />
      </Box>
      {body.exchange_notes ? (
        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
          {body.exchange_notes}
        </Typography>
      ) : null}
      {primaryTdpId ? (
        <Stack direction="row" justifyContent="flex-end">
          <Button size="small" variant="outlined" onClick={() => window.open(`/tdp/${primaryTdpId}`, '_blank', 'noopener,noreferrer')}>
            Open xTDP
          </Button>
        </Stack>
      ) : (
        <ExchangeCardContent card={card} />
      )}
    </Stack>
  );
};

const XTdpPullDialog = ({
  open,
  tdps = [],
  loading = false,
  busy = false,
  onClose,
  onAttach,
}) => {
  const [selectedTdpId, setSelectedTdpId] = useState('');
  const [exchangeNotes, setExchangeNotes] = useState('');

  React.useEffect(() => {
    if (!open) {
      setSelectedTdpId('');
      setExchangeNotes('');
    }
  }, [open]);

  const selectedTdp = tdps.find((tdp) => tdp.tdp_id === selectedTdpId) || null;

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Attach xTDP</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {loading ? <LinearProgress /> : null}
          <TextField
            select
            label="Technical Data Package"
            value={selectedTdpId}
            onChange={(event) => setSelectedTdpId(event.target.value)}
            disabled={busy || loading || !tdps.length}
            fullWidth
          >
            {tdps.map((tdp) => (
              <MenuItem key={tdp.tdp_id} value={tdp.tdp_id}>
                {tdpOptionLabel(tdp)}
              </MenuItem>
            ))}
          </TextField>
          {!loading && !tdps.length ? (
            <Alert severity="info">
              No owned or shared xTDPs are available to attach.
            </Alert>
          ) : null}
          <TextField
            label="Exchange notes"
            value={exchangeNotes}
            onChange={(event) => setExchangeNotes(event.target.value)}
            disabled={busy}
            multiline
            minRows={3}
            fullWidth
          />
          {selectedTdp ? (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 1 }}>
              <MetaBlock label="Status" value={getStatusLabel(selectedTdp.status)} />
              <MetaBlock label="Version" value={selectedTdp.version || 'Unversioned'} />
            </Box>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => onAttach?.(selectedTdp, exchangeNotes)}
          disabled={busy || loading || !selectedTdp}
        >
          {busy ? 'Attaching...' : 'Attach'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const getSchemaBodyFields = (schema = {}) => (
  Array.isArray(schema.body) ? schema.body.filter((field) => field && typeof field === 'object') : []
);

const SchemaFieldDefinitionList = ({ schema }) => {
  const fields = getSchemaBodyFields(schema);
  if (!fields.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        No schema fields have been configured for this Exchange Card.
      </Typography>
    );
  }

  return (
    <Stack spacing={1}>
      {fields.map((field) => (
        <Box
          key={field.name || field.label}
          sx={{
            p: 1.25,
            borderRadius: 1.5,
            border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.72)}`,
            bgcolor: (theme) => alpha(theme.palette.background.default, 0.38),
          }}
        >
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between">
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 850, wordBreak: 'break-word' }}>
                {field.label || field.name}
              </Typography>
              {field.description ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35, whiteSpace: 'pre-wrap' }}>
                  {field.description}
                </Typography>
              ) : null}
            </Box>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              {field.type ? <Chip size="small" variant="outlined" label={field.type} /> : null}
              {field.required ? <Chip size="small" color="primary" variant="outlined" label="Required" /> : null}
            </Stack>
          </Stack>
        </Box>
      ))}
    </Stack>
  );
};

const getSchemaFieldName = (field = {}) => String(field.name || field.key || '').trim();

const getSchemaFieldLabel = (field = {}) => (
  field.label || formatCategoryLabel(getSchemaFieldName(field)) || 'Field'
);

const getSchemaField = (schema = {}, fieldName) => (
  getSchemaBodyFields(schema).find((field) => getSchemaFieldName(field) === fieldName) || null
);

const getSchemaFieldType = (field = {}) => (
  String(field.type || 'text').trim().toLowerCase() || 'text'
);

const normalizeFieldOptions = (field = {}) => {
  const options = Array.isArray(field.options)
    ? field.options
    : Array.isArray(field.enum)
      ? field.enum
      : Array.isArray(field.choices)
        ? field.choices
        : [];

  return options
    .map((option) => {
      if (option && typeof option === 'object') {
        const value = option.value ?? option.key ?? option.id ?? option.name ?? option.label;
        if (value === null || typeof value === 'undefined') {
          return null;
        }
        return {
          value,
          label: option.label || option.title || option.name || String(value),
        };
      }
      return {
        value: option,
        label: String(option),
      };
    })
    .filter(Boolean);
};

const isBlankSchemaValue = (value) => (
  value === null ||
  typeof value === 'undefined' ||
  (typeof value === 'string' && !value.trim()) ||
  (Array.isArray(value) && !value.length)
);

const defaultSchemaFieldValue = (field = {}, fallback) => {
  if (typeof fallback !== 'undefined') {
    return fallback;
  }

  if (typeof field.default !== 'undefined') {
    return field.default;
  }

  const fieldType = getSchemaFieldType(field);
  if (fieldType === 'boolean' || fieldType === 'instruction') {
    return false;
  }
  if (fieldType === 'multiselect' || fieldType === 'tags') {
    return [];
  }
  return '';
};

const buildExchangeCardFormValues = (schema = {}, body = {}, defaults = {}) => {
  const fields = getSchemaBodyFields(schema);
  return fields.reduce((values, field) => {
    const name = getSchemaFieldName(field);
    if (!name) {
      return values;
    }
    const hasBodyValue = Object.prototype.hasOwnProperty.call(body || {}, name);
    const hasDefaultValue = Object.prototype.hasOwnProperty.call(defaults || {}, name);
    values[name] = defaultSchemaFieldValue(
      field,
      hasBodyValue ? body[name] : hasDefaultValue ? defaults[name] : undefined,
    );
    return values;
  }, { ...(defaults || {}) });
};

const normalizeExchangeFieldValue = (field = {}, value) => {
  const fieldType = getSchemaFieldType(field);
  if (fieldType === 'boolean' || fieldType === 'instruction') {
    return Boolean(value);
  }
  if (fieldType === 'number' || fieldType === 'range') {
    if (value === '' || value === null || typeof value === 'undefined') {
      return '';
    }
    const numericValue = Number(value);
    return Number.isNaN(numericValue) ? value : numericValue;
  }
  if (fieldType === 'multiselect' || fieldType === 'tags') {
    if (Array.isArray(value)) {
      return value;
    }
    return String(value || '')
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  }
  return value;
};

const setExchangeCardFormValue = (setValues, field = {}, value) => {
  const name = getSchemaFieldName(field);
  if (!name) {
    return;
  }
  setValues((current) => ({
    ...current,
    [name]: normalizeExchangeFieldValue(field, value),
  }));
};

const isSchemaFieldComplete = (field = {}, value) => {
  if (!field.required) {
    return true;
  }

  const fieldType = getSchemaFieldType(field);
  if (fieldType === 'boolean' || fieldType === 'instruction') {
    return value === true;
  }
  if (fieldType === 'number' || fieldType === 'range') {
    return value !== '' && value !== null && typeof value !== 'undefined' && !Number.isNaN(Number(value));
  }
  return !isBlankSchemaValue(value);
};

const getMissingRequiredSchemaFields = (schema = {}, values = {}) => (
  getSchemaBodyFields(schema).filter((field) => (
    !isSchemaFieldComplete(field, values[getSchemaFieldName(field)])
  ))
);

const isExchangeCardFormComplete = (schema = {}, values = {}) => (
  getMissingRequiredSchemaFields(schema, values).length === 0
);

const sanitizeExchangeCardFormValues = (schema = {}, values = {}, { includeCompletionStamp = true } = {}) => {
  const body = {};
  getSchemaBodyFields(schema).forEach((field) => {
    const name = getSchemaFieldName(field);
    if (!name) {
      return;
    }
    const fieldType = getSchemaFieldType(field);
    const value = normalizeExchangeFieldValue(field, values[name]);
    if (!field.required && isBlankSchemaValue(value)) {
      return;
    }
    if ((fieldType === 'number' || fieldType === 'range') && value !== '' && !Number.isNaN(Number(value))) {
      body[name] = Number(value);
      return;
    }
    body[name] = value;
  });

  if (includeCompletionStamp) {
    if (!Object.prototype.hasOwnProperty.call(body, 'confirmed')) {
      body.confirmed = true;
    }
    if (!body.confirmed_at) {
      body.confirmed_at = new Date().toISOString();
    }
  }

  return body;
};

const ExchangeCardSchemaInput = ({ field, value, onChange, disabled = false }) => {
  const name = getSchemaFieldName(field);
  const fieldType = getSchemaFieldType(field);
  const label = getSchemaFieldLabel(field);
  const helperText = String(field.description || field.helper_text || ' ');
  const options = normalizeFieldOptions(field);
  const commonTextProps = {
    key: name,
    size: 'small',
    label,
    value: value ?? '',
    onChange: (event) => onChange(normalizeExchangeFieldValue(field, event.target.value)),
    disabled,
    required: Boolean(field.required),
    helperText,
    fullWidth: true,
  };

  if (fieldType === 'boolean' || fieldType === 'instruction') {
    const Control = fieldType === 'instruction' || field.required ? Checkbox : Switch;
    return (
      <Box
        key={name}
        sx={{
          p: 1.25,
          borderRadius: 1.5,
          border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.72)}`,
          bgcolor: (theme) => alpha(theme.palette.background.default, 0.38),
        }}
      >
        <FormControlLabel
          control={(
            <Control
              checked={Boolean(value)}
              onChange={(event) => onChange(event.target.checked)}
              disabled={disabled}
            />
          )}
          label={label}
          sx={{
            alignItems: 'flex-start',
            mr: 0,
            '& .MuiFormControlLabel-label': {
              pt: fieldType === 'instruction' || field.required ? 0.95 : 0.45,
              fontWeight: 800,
            },
          }}
        />
        {helperText.trim() ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35, pl: 4.1, whiteSpace: 'pre-wrap' }}>
            {helperText}
          </Typography>
        ) : null}
      </Box>
    );
  }

  if (fieldType === 'select' && options.length) {
    return (
      <TextField {...commonTextProps} select>
        {options.map((option) => (
          <MenuItem key={String(option.value)} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>
    );
  }

  if (fieldType === 'multiselect' || fieldType === 'tags') {
    const selectedValues = Array.isArray(value) ? value : [];
    if (!options.length) {
      return (
        <TextField
          {...commonTextProps}
          value={selectedValues.join(', ')}
          onChange={(event) => onChange(normalizeExchangeFieldValue(field, event.target.value))}
        />
      );
    }
    return (
      <FormControl key={name} fullWidth size="small" required={Boolean(field.required)} disabled={disabled}>
        <InputLabel id={`${name}-exchange-card-field-label`}>{label}</InputLabel>
        <Select
          labelId={`${name}-exchange-card-field-label`}
          multiple
          label={label}
          value={selectedValues}
          input={<OutlinedInput label={label} />}
          onChange={(event) => onChange(event.target.value)}
          renderValue={(selected) => selected.join(', ')}
        >
          {options.map((option) => (
            <MenuItem key={String(option.value)} value={option.value}>
              <Checkbox checked={selectedValues.includes(option.value)} />
              <ListItemText primary={option.label} />
            </MenuItem>
          ))}
        </Select>
        {helperText.trim() ? <FormHelperText>{helperText}</FormHelperText> : null}
      </FormControl>
    );
  }

  if (fieldType === 'textarea') {
    return <TextField {...commonTextProps} multiline minRows={3} />;
  }

  if (fieldType === 'number' || fieldType === 'range') {
    return (
      <TextField
        {...commonTextProps}
        type="number"
        inputProps={{
          step: field.step || 'any',
          min: field.min ?? field.minimum,
          max: field.max ?? field.maximum,
        }}
      />
    );
  }

  if (fieldType === 'date' || fieldType === 'datetime') {
    return (
      <TextField
        {...commonTextProps}
        type={fieldType === 'datetime' ? 'datetime-local' : 'date'}
        InputLabelProps={{ shrink: true }}
      />
    );
  }

  if (fieldType === 'file') {
    return (
      <TextField
        {...commonTextProps}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <UploadFileRoundedIcon fontSize="small" color="disabled" />
            </InputAdornment>
          ),
        }}
      />
    );
  }

  return <TextField {...commonTextProps} type={fieldType === 'email' || fieldType === 'url' ? fieldType : 'text'} />;
};

const ExchangeCardBodyForm = ({
  schema,
  values,
  onChange,
  disabled = false,
  hiddenFields = [],
  renderField = null,
}) => {
  const hidden = new Set(hiddenFields);
  const fields = getSchemaBodyFields(schema).filter((field) => {
    const name = getSchemaFieldName(field);
    return name && !hidden.has(name);
  });

  if (!fields.length) {
    return null;
  }

  return (
    <Stack spacing={1.1}>
      {fields.map((field) => {
        const name = getSchemaFieldName(field);
        const customField = renderField?.({ field, value: values[name] });
        if (customField) {
          return customField;
        }
        return (
          <ExchangeCardSchemaInput
            key={name}
            field={field}
            value={values[name]}
            disabled={disabled}
            onChange={(value) => onChange(field, value)}
          />
        );
      })}
    </Stack>
  );
};

const MissingRequiredFieldsAlert = ({ fields = [] }) => {
  if (!fields.length) {
    return null;
  }

  return (
    <Alert severity="info">
      Complete required fields: {fields.map((field) => getSchemaFieldLabel(field)).join(', ')}.
    </Alert>
  );
};

const ExchangeCardToolbar = ({
  card,
  busy = false,
  onPublish,
  onRemove,
  onShare,
  onEdit,
  onExport,
  onHistory,
}) => {
  const canRemove = (
    card?.can_remove === true ||
    (
      card?.can_remove === undefined &&
      card?.status !== 'completed' &&
      card?.can_edit !== false
    )
  );

  return (
    <Stack spacing={0.75} sx={{ minWidth: { xs: '100%', sm: 150 } }}>
      {card?.can_publish ? (
        <Button
          size="small"
          variant="contained"
          startIcon={<PublishRoundedIcon />}
          disabled={busy || !onPublish}
          onClick={() => onPublish?.(card)}
        >
          Publish
        </Button>
      ) : null}
      <Button
        size="small"
        variant="outlined"
        startIcon={<DownloadRoundedIcon />}
        disabled={busy || !onExport}
        onClick={() => onExport?.(card)}
      >
        Export
      </Button>
      <Button
        size="small"
        variant="outlined"
        startIcon={<ShareRoundedIcon />}
        disabled={busy || !onShare}
        onClick={() => onShare?.(card)}
      >
        Share
      </Button>
      <Button
        size="small"
        variant="outlined"
        startIcon={<EditRoundedIcon />}
        disabled={busy || !card?.can_edit || !onEdit}
        onClick={() => onEdit?.(card)}
      >
        Edit
      </Button>
      {canRemove ? (
        <Button
          size="small"
          variant="outlined"
          color="error"
          startIcon={<DeleteRoundedIcon />}
          disabled={busy || !onRemove}
          onClick={() => onRemove?.(card)}
        >
          Remove
        </Button>
      ) : null}
      {card?.revision_count ? (
        <Button
          size="small"
          variant="text"
          startIcon={<HistoryRoundedIcon />}
          disabled={busy || !onHistory}
          onClick={() => onHistory?.(card)}
        >
          Edited
        </Button>
      ) : null}
    </Stack>
  );
};

const ExchangeCardContent = ({
  card,
  children = null,
  showSchemaDefinitions = true,
  showSubmittedValues = true,
}) => {
  const schema = card.schema || {};
  const body = card.body || {};
  const hasBodyValues = Object.keys(body).length > 0;

  return (
    <Stack spacing={1.5}>
      {schema.load_error ? (
        <Alert severity="warning">
          This Exchange Card references a schema that could not be loaded: {schema.load_error}
        </Alert>
      ) : null}
      {card.description ? (
        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
          {card.description}
        </Typography>
      ) : null}
      <Box sx={{ minWidth: 0 }}>
        {children || (showSchemaDefinitions ? <SchemaFieldDefinitionList schema={schema} /> : null)}
        {showSubmittedValues && hasBodyValues ? (
          <Box sx={{ mt: 1.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 850, mb: 0.75 }}>
              Submitted Values
            </Typography>
            <SchemaBodyFields body={body} schema={schema} hiddenFields={['confirmed_at']} compact />
          </Box>
        ) : null}
      </Box>
    </Stack>
  );
};

const exchangeCardScopeGroups = [
  { scope: 'thread', title: 'Thread Exchange Cards' },
  { scope: 'capability', title: 'Capability Exchange Cards' },
  { scope: 'asset', title: 'Asset Exchange Cards' },
  { scope: 'other', title: 'Other Exchange Cards' },
];

const normalizeExchangeCardScope = (value) => {
  const scope = String(value || '').toLowerCase();
  if (scope === 'workspace' || scope === 'thread') {
    return 'thread';
  }
  if (scope === 'capability') {
    return 'capability';
  }
  if (scope === 'asset' || scope === 'targeted') {
    return 'asset';
  }
  return 'other';
};

const exchangeCardScopeLabel = (scope) => (
  exchangeCardScopeGroups.find((group) => group.scope === scope)?.title || 'Other Exchange Cards'
);

const AvailableExchangeCardCandidate = ({
  candidate,
  actionKey,
  onPullExchangeCard,
  onDragStart,
  onDragEnd,
  disabled = false,
}) => {
  const busy = actionKey === `pull:${candidate.card_key}`;
  const canDrag = Boolean(!disabled && candidate.can_pull && !busy && onPullExchangeCard);
  const scope = normalizeExchangeCardScope(candidate.scope || candidate.target_scope);

  return (
    <Box
      draggable={canDrag}
      onDragStart={(event) => {
        if (!canDrag) {
          event.preventDefault();
          return;
        }
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData(EXCHANGE_CARD_DRAG_TYPE, candidate.card_key);
        event.dataTransfer.setData('text/plain', candidate.title || candidate.card_key);
        onDragStart?.(candidate);
      }}
      onDragEnd={() => onDragEnd?.()}
      sx={{
        p: 1.35,
        borderRadius: 1.5,
        border: (theme) => `1px solid ${alpha(candidate.blocked ? theme.palette.warning.main : theme.palette.primary.main, candidate.blocked ? 0.36 : 0.24)}`,
        bgcolor: (theme) => alpha(candidate.blocked ? theme.palette.warning.main : theme.palette.primary.main, candidate.blocked ? 0.045 : 0.035),
        cursor: canDrag ? 'grab' : 'default',
        opacity: busy ? 0.68 : 1,
        transition: 'border-color 160ms ease, background-color 160ms ease, transform 160ms ease, opacity 160ms ease',
        '&:active': {
          cursor: canDrag ? 'grabbing' : 'default',
        },
        ...(canDrag
          ? {
              '&:hover': {
                borderColor: (theme) => alpha(theme.palette.primary.main, 0.5),
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06),
                transform: 'translateY(-1px)',
              },
            }
          : {}),
      }}
    >
      <Stack spacing={1}>
        {busy ? <LinearProgress /> : null}
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} justifyContent="space-between">
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              {candidate.blocked ? <LockRoundedIcon color="warning" fontSize="small" /> : <AddCircleRoundedIcon color="primary" fontSize="small" />}
              <Typography variant="subtitle2" sx={{ fontWeight: 900, wordBreak: 'break-word' }}>
                {candidate.title}
              </Typography>
              {candidate.is_required ? <Chip size="small" color="primary" variant="outlined" label="Required" /> : null}
              <Chip size="small" variant="outlined" label={exchangeCardScopeLabel(scope).replace(' Exchange Cards', '')} />
            </Stack>
            {candidate.description ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {candidate.description}
              </Typography>
            ) : null}
            {candidate.blocked ? (
              <Alert severity="info" sx={{ mt: 1 }}>
                {candidate.blocked_reason}
              </Alert>
            ) : null}
          </Box>
          <Chip
            size="small"
            color={busy ? 'primary' : canDrag ? 'success' : candidate.blocked ? 'warning' : 'default'}
            variant={canDrag || busy ? 'filled' : 'outlined'}
            label={busy ? 'Adding...' : canDrag ? 'Drag to timeline' : candidate.blocked ? 'Blocked' : 'Unavailable'}
            sx={{ alignSelf: { xs: 'flex-start', md: 'flex-start' }, fontWeight: 800 }}
          />
        </Stack>
      </Stack>
    </Box>
  );
};

const AvailableExchangeCardsCard = ({
  card,
  actionKey,
  onPullExchangeCard,
  onDragStart,
  onDragEnd,
  disabled = false,
}) => {
  const cards = card.cards || [];
  const [openGroups, setOpenGroups] = useState(() => ({
    thread: true,
    capability: true,
    asset: true,
    other: true,
  }));
  const groupedCards = exchangeCardScopeGroups
    .map((group) => ({
      ...group,
      cards: cards.filter((candidate) => normalizeExchangeCardScope(candidate.scope || candidate.target_scope) === group.scope),
    }))
    .filter((group) => group.cards.length);

  return (
    <Stack spacing={1.5}>
      {card.selected_asset ? (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 1 }}>
          <MetaBlock label="Selected Asset" value={card.selected_asset.asset_name} />
          <MetaBlock label="Asset Source ID" value={card.selected_asset.source_capability_asset_id} />
        </Box>
      ) : null}
      {!cards.length ? (
        <Typography variant="body2" color="text.secondary">
          No Exchange Cards are available for this thread.
        </Typography>
      ) : null}
      {groupedCards.map((group, index) => (
        <Stack key={group.scope} spacing={1}>
          {index > 0 ? <Divider /> : null}
          <Button
            variant="text"
            color="inherit"
            onClick={() => setOpenGroups((current) => ({ ...current, [group.scope]: !current[group.scope] }))}
            endIcon={(
              <ExpandMoreRoundedIcon
                fontSize="small"
                sx={{
                  transform: openGroups[group.scope] ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 160ms ease',
                }}
              />
            )}
            sx={{
              justifyContent: 'space-between',
              px: 0.25,
              color: 'text.secondary',
              textTransform: 'none',
              fontWeight: 900,
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="overline" sx={{ fontWeight: 900, letterSpacing: 0 }}>
                {group.title}
              </Typography>
              <Chip size="small" variant="outlined" label={group.cards.length} />
            </Stack>
          </Button>
          <Collapse in={openGroups[group.scope]} timeout="auto" unmountOnExit>
            <Stack spacing={1}>
              {group.cards.map((candidate) => (
                <AvailableExchangeCardCandidate
                  key={candidate.rule_id || candidate.card_key}
                  candidate={candidate}
                  actionKey={actionKey}
                  onPullExchangeCard={onPullExchangeCard}
                  onDragStart={onDragStart}
                  onDragEnd={onDragEnd}
                  disabled={disabled}
                />
              ))}
            </Stack>
          </Collapse>
        </Stack>
      ))}
    </Stack>
  );
};

const ManagedAssetAttachment = ({
  card,
  actionKey,
  managedAssets = [],
  managedAssetsLoading = false,
  onCompleteExchangeCard,
  disabled = false,
}) => {
  const selectedAsset = card.body?.selected_asset || null;
  const schema = card.schema || {};
  const assetIdField = getSchemaField(schema, 'asset_id') || {
    name: 'asset_id',
    label: 'Managed Asset',
    required: true,
    type: 'select',
  };
  const [formValues, setFormValues] = useState(() => buildExchangeCardFormValues(schema, card.body || {}, {
    asset_id: card.body?.asset_id || '',
    attachment_confirmed: Boolean(card.body?.attachment_confirmed),
  }));
  const busy = actionKey === `complete:${card.card_key}`;
  const canAttach = card.can_complete && !selectedAsset;
  const assetId = formValues.asset_id || '';
  const missingFields = getMissingRequiredSchemaFields(schema, formValues);

  React.useEffect(() => {
    setFormValues(buildExchangeCardFormValues(schema, card.body || {}, {
      asset_id: card.body?.asset_id || '',
      attachment_confirmed: Boolean(card.body?.attachment_confirmed),
    }));
  }, [card.card_key, card.status, card.body?.asset_id, card.body?.attachment_confirmed]);

  if (selectedAsset) {
    return (
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 1 }}>
        <MetaBlock label="Attached Asset" value={selectedAsset.asset_name || card.body?.asset_name} />
        <MetaBlock label="Capability" value={selectedAsset.capability_name || card.body?.capability_name} />
        <MetaBlock label="Asset Source ID" value={selectedAsset.source_capability_asset_id || card.body?.source_capability_asset_id} />
        <MetaBlock label="Partner ID" value={selectedAsset.partner_id || card.body?.partner_id} />
      </Box>
    );
  }

  return (
    <Stack spacing={1.5}>
      {canAttach ? (
        <ExchangeCardContent card={card} showSchemaDefinitions={false} showSubmittedValues={false}>
          <Stack spacing={1.1}>
            <TextField
              select
              size="small"
              label={getSchemaFieldLabel(assetIdField)}
              value={assetId}
              onChange={(event) => setExchangeCardFormValue(setFormValues, assetIdField, event.target.value)}
              disabled={disabled || managedAssetsLoading || busy}
              required={Boolean(assetIdField.required)}
              helperText={assetIdField.description || ' '}
              fullWidth
            >
              {managedAssets.map((asset) => (
                <MenuItem key={asset.asset_id} value={asset.asset_id}>
                  {asset.asset_name || asset.source_capability_asset_id || asset.asset_id}
                </MenuItem>
              ))}
            </TextField>
            <ExchangeCardBodyForm
              schema={schema}
              values={formValues}
              onChange={(field, value) => setExchangeCardFormValue(setFormValues, field, value)}
              disabled={disabled || busy}
              hiddenFields={['asset_id']}
            />
            {!managedAssetsLoading && !managedAssets.length ? (
              <Alert severity="info">
                No managed assets are available for your associated partner/business unit yet.
              </Alert>
            ) : null}
            <MissingRequiredFieldsAlert fields={missingFields} />
            <Stack direction="row" justifyContent="flex-end">
              <Button
                variant="contained"
                startIcon={<CheckCircleRoundedIcon />}
                disabled={
                  disabled ||
                  busy ||
                  managedAssetsLoading ||
                  !assetId ||
                  !isExchangeCardFormComplete(schema, formValues) ||
                  !onCompleteExchangeCard
                }
                onClick={() => onCompleteExchangeCard?.(card, {
                  asset_id: assetId,
                  attachment_confirmed: Boolean(formValues.attachment_confirmed),
                })}
              >
                {busy ? 'Attaching...' : 'Attach Asset'}
              </Button>
            </Stack>
          </Stack>
        </ExchangeCardContent>
      ) : (
        <ExchangeCardContent card={card} />
      )}
    </Stack>
  );
};

const getReferenceUri = (reference = {}) => (
  reference.uri ||
  reference.source_uri ||
  reference.sourceUri ||
  reference.s3_uri ||
  reference.s3Uri ||
  reference.url ||
  ''
);

const getReferenceText = (reference = {}) => (
  reference.text ||
  reference.excerpt ||
  reference.quote ||
  reference.snippet ||
  reference.content ||
  ''
);

const getReferenceTitle = (reference = {}, index = 0) => {
  const uri = getReferenceUri(reference);
  const fromUri = uri ? uri.split('/').filter(Boolean).pop() : '';
  return (
    reference.title ||
    reference.name ||
    reference.filename ||
    reference.file_name ||
    reference.document_name ||
    fromUri ||
    `Source ${index + 1}`
  );
};

const isExternalUrl = (value = '') => /^https?:\/\//i.test(String(value || ''));

const EddieReferenceList = ({ references = [] }) => {
  const visibleReferences = references.filter((reference) => getReferenceUri(reference) || getReferenceText(reference)).slice(0, 4);
  if (!visibleReferences.length) {
    return null;
  }

  return (
    <Stack spacing={0.75} sx={{ mt: 1.25 }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 850 }}>
        Source Documents
      </Typography>
      <Stack spacing={0.75}>
        {visibleReferences.map((reference, index) => {
          const uri = getReferenceUri(reference);
          const text = getReferenceText(reference);
          const title = getReferenceTitle(reference, index);
          const label = String(title).replace(/^s3:\/\//, '');
          return (
            <Box
              key={`${label}-${index}`}
              sx={{
                p: 1,
                borderRadius: 1,
                border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.72)}`,
                bgcolor: (theme) => alpha(theme.palette.background.paper, 0.62),
              }}
            >
              <Stack spacing={0.5}>
                <Stack direction="row" spacing={0.75} alignItems="center" justifyContent="space-between">
                  <Typography variant="caption" sx={{ fontWeight: 900, wordBreak: 'break-word' }}>
                    {label}
                  </Typography>
                  {isExternalUrl(uri) ? (
                    <Button
                      size="small"
                      variant="text"
                      component="a"
                      href={uri}
                      target="_blank"
                      rel="noreferrer"
                      sx={{ minWidth: 0, px: 0.75 }}
                    >
                      Open
                    </Button>
                  ) : null}
                </Stack>
                {text ? (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', whiteSpace: 'pre-wrap' }}>
                    {String(text).slice(0, 240)}
                  </Typography>
                ) : null}
                {uri ? (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', wordBreak: 'break-all' }}>
                    {String(uri).replace(/^s3:\/\//, '')}
                  </Typography>
                ) : null}
              </Stack>
            </Box>
          );
        })}
      </Stack>
    </Stack>
  );
};

const formatRunEventType = (value = '') => (
  String(value || '')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (match) => match.toUpperCase())
);

const runStatusColor = (status = '') => {
  const normalized = String(status || '').toLowerCase();
  if (normalized === 'completed') {
    return 'success';
  }
  if (normalized === 'failed') {
    return 'error';
  }
  if (normalized === 'warning') {
    return 'warning';
  }
  return 'info';
};

const EddieRunTimeline = ({ lastRun = {}, events = [] }) => {
  const runId = lastRun?.run_id;
  const visibleEvents = (Array.isArray(events) ? events : [])
    .filter((event) => !runId || event.run_id === runId)
    .slice(-6);
  if (!runId && !visibleEvents.length) {
    return null;
  }
  const status = lastRun?.status || visibleEvents[visibleEvents.length - 1]?.status || 'running';
  const documentBacked = lastRun?.document_backed === true;

  return (
    <Box
      sx={{
        p: 1.25,
        borderRadius: 1.5,
        border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.78)}`,
        bgcolor: (theme) => alpha(theme.palette.background.default, 0.32),
      }}
    >
      <Stack spacing={1}>
        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
          <Chip
            size="small"
            icon={<HistoryRoundedIcon />}
            label={`Run ${formatRunEventType(status)}`}
            color={runStatusColor(status)}
            variant="outlined"
          />
          {documentBacked ? (
            <Chip size="small" label="Document-backed" color="success" variant="outlined" />
          ) : null}
        </Stack>
        {status === 'running' ? <LinearProgress /> : null}
        {visibleEvents.length ? (
          <Stack spacing={0.5}>
            {visibleEvents.map((event, index) => (
              <Stack
                key={event.event_id || `${event.type}-${index}`}
                direction={{ xs: 'column', sm: 'row' }}
                spacing={{ xs: 0.25, sm: 1 }}
                justifyContent="space-between"
                sx={{ minWidth: 0 }}
              >
                <Typography variant="caption" sx={{ fontWeight: 850 }}>
                  {formatRunEventType(event.type)}
                  {event.step ? ` - ${formatRunEventType(event.step)}` : ''}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ flex: 1, minWidth: 0, wordBreak: 'break-word' }}>
                  {event.message || event.summary || ''}
                </Typography>
              </Stack>
            ))}
          </Stack>
        ) : null}
      </Stack>
    </Box>
  );
};

const EddieMessage = ({ message }) => {
  const isUser = message.role === 'user';
  const status = String(message.status || '').toLowerCase();
  const isFailed = status === 'failed';
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: isUser ? 'flex-end' : 'flex-start',
      }}
    >
      <Box
        sx={{
          width: 'fit-content',
          maxWidth: { xs: '100%', md: '82%' },
          borderRadius: 1.5,
          border: (theme) => `1px solid ${alpha(isFailed ? theme.palette.error.main : isUser ? theme.palette.primary.main : theme.palette.divider, isFailed ? 0.5 : isUser ? 0.32 : 0.82)}`,
          bgcolor: (theme) => isFailed ? alpha(theme.palette.error.main, 0.08) : isUser ? alpha(theme.palette.primary.main, 0.08) : alpha(theme.palette.background.default, 0.58),
          p: 1.25,
        }}
      >
        <Stack spacing={0.5}>
          <Stack direction="row" spacing={0.75} alignItems="center" justifyContent="space-between">
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 900 }}>
              {isUser ? 'You' : 'EDDIE'}
            </Typography>
            {message.created_at ? (
              <Typography variant="caption" color="text.secondary">
                {formatDateTime(message.created_at)}
              </Typography>
            ) : null}
          </Stack>
          {status && status !== 'completed' && status !== 'submitted' ? (
            <Chip
              size="small"
              label={formatRunEventType(status)}
              color={runStatusColor(status)}
              variant="outlined"
              sx={{ alignSelf: 'flex-start' }}
            />
          ) : null}
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {message.content}
          </Typography>
          {!isUser ? <EddieReferenceList references={Array.isArray(message.references) ? message.references : []} /> : null}
        </Stack>
      </Box>
    </Box>
  );
};

const EddieAssistanceCard = ({
  card,
  actionKey,
  onSendEddieMessage,
  disabled = false,
}) => {
  const body = card.body || {};
  const messages = Array.isArray(body.messages) ? body.messages : [];
  const runEvents = Array.isArray(body.run_events) ? body.run_events : [];
  const lastRun = body.last_run || {};
  const asset = body.asset || card.asset || {};
  const [messageText, setMessageText] = useState('');
  const busy = actionKey === `chat:${card.card_key}`;
  const canSend = !disabled && !busy && Boolean(onSendEddieMessage) && messageText.trim();

  React.useEffect(() => {
    setMessageText('');
  }, [card.card_key, messages.length]);

  const sendMessage = () => {
    const trimmed = messageText.trim();
    if (!trimmed) {
      return;
    }
    onSendEddieMessage?.(card, trimmed);
  };

  return (
    <ExchangeCardContent card={card} showSchemaDefinitions={false} showSubmittedValues={false}>
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
          <Chip
            size="small"
            icon={<EngineeringRoundedIcon />}
            label={body.assistant_name || 'EDDIE'}
            color="primary"
            variant="outlined"
          />
          {asset.asset_name ? (
            <Chip size="small" label={asset.asset_name} variant="outlined" />
          ) : null}
          {body.knowledge_base?.s3_uri_prefix ? (
            <Chip size="small" label="Optomec KB" color="success" variant="outlined" />
          ) : null}
        </Stack>

        <EddieRunTimeline lastRun={lastRun} events={runEvents} />

        <Stack
          spacing={1}
          sx={{
            maxHeight: { xs: 360, md: 460 },
            overflowY: 'auto',
            borderRadius: 1.5,
            border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.78)}`,
            bgcolor: (theme) => alpha(theme.palette.background.default, 0.32),
            p: 1,
          }}
        >
          {messages.length ? (
            messages.map((message, index) => (
              <EddieMessage key={message.message_id || `${message.role}-${index}`} message={message} />
            ))
          ) : (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <EngineeringRoundedIcon color="disabled" />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                EDDIE is ready for this asset.
              </Typography>
            </Box>
          )}
        </Stack>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ xs: 'stretch', md: 'flex-end' }}>
          <TextField
            value={messageText}
            onChange={(event) => setMessageText(event.target.value)}
            onKeyDown={(event) => {
              if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
                event.preventDefault();
                if (canSend) {
                  sendMessage();
                }
              }
            }}
            label="Message"
            placeholder="Ask EDDIE"
            size="small"
            multiline
            minRows={2}
            maxRows={5}
            disabled={disabled || busy}
            fullWidth
          />
          <Button
            variant="contained"
            startIcon={<SendRoundedIcon />}
            disabled={!canSend}
            onClick={sendMessage}
            sx={{ minWidth: { xs: '100%', md: 132 } }}
          >
            {busy ? 'Asking...' : 'Ask EDDIE'}
          </Button>
        </Stack>
      </Stack>
    </ExchangeCardContent>
  );
};

const isPrintableFile = (file = {}) => {
  const filename = String(file.filename || '').toLowerCase();
  return filename.endsWith('.gcode') || filename.endsWith('.bgcode');
};

const formatPrintStatus = (value = '') => (
  String(value || 'unknown')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (match) => match.toUpperCase())
);

const activePrintJobStatuses = new Set([
  'file_selected',
  'prepare_command_sent',
  'file_downloading',
  'file_uploaded_to_printer',
  'ready_to_start',
  'start_command_sent',
  'printing',
  'paused',
  'completed',
  'pickup_pending',
]);

const systemPrintEventCards = new Set([
  'printer_file_ready',
  'print_job_started',
  'print_job_status_update',
  'print_job_completed',
  'print_job_error',
]);

const telemetryExchangeCardKeys = new Set([
  'view_telemetry',
  'view_telemtry',
]);

const threadFileVisualizationConfigs = {
  view_2d_drafting_format: {
    title: '2D Drafting Preview',
    visualizationType: 'drafting_2d',
    supportedLabel: '.dwg, .dxf',
    fileFields: [
      {
        name: 'filename',
        label: '2D drafting file',
        allowedExtensions: ['.dwg', '.dxf'],
      },
    ],
    emptyMessage: 'Upload a .dwg or .dxf file in Thread Files before visualizing this drafting card.',
    completeLabel: 'Visualize Drafting File',
  },
  view_3d_solid_format: {
    title: '3D Solid Preview',
    visualizationType: 'solid_3d',
    supportedLabel: '.stp, .step, .igs, .iges, .x_t, .x_b',
    fileFields: [
      {
        name: 'filename',
        label: '3D solid file',
        allowedExtensions: ['.stp', '.step', '.igs', '.iges', '.x_t', '.x_b'],
      },
    ],
    emptyMessage: 'Upload a STEP, IGES, or Parasolid file in Thread Files before visualizing this solid card.',
    completeLabel: 'Visualize Solid File',
  },
  view_mesh_format: {
    title: 'Mesh Preview',
    visualizationType: 'mesh_3d',
    supportedLabel: '.stl, .stil, .obj, .3mf',
    fileFields: [
      {
        name: 'filename',
        label: 'Mesh file',
        allowedExtensions: ['.stl', '.stil', '.obj', '.3mf'],
      },
    ],
    emptyMessage: 'Upload a .stl, .stil, .obj, or .3mf file in Thread Files before visualizing this mesh card.',
    completeLabel: 'Visualize Mesh File',
  },
  view_bim_architecture_format: {
    title: 'BIM/Architecture Preview',
    visualizationType: 'bim_architecture',
    supportedLabel: '.rvt, .ifc',
    fileFields: [
      {
        name: 'filename',
        label: 'BIM or architecture file',
        allowedExtensions: ['.rvt', '.ifc'],
      },
    ],
    emptyMessage: 'Upload a .rvt or .ifc file in Thread Files before visualizing this architecture card.',
    completeLabel: 'Visualize Architecture File',
  },
  view_gcode: {
    title: 'GCode Toolpath Preview',
    visualizationType: 'gcode_toolpath',
    supportedLabel: '.gcode, .bgcode',
    fileFields: [
      {
        name: 'filename',
        label: 'GCode file',
        allowedExtensions: ['.gcode', '.bgcode'],
      },
    ],
    emptyMessage: 'Upload a .gcode or .bgcode file in Thread Files before visualizing this GCode card.',
    completeLabel: 'Visualize GCode',
  },
  view_3d_scanned_model: {
    title: '3D Scan Preview',
    visualizationType: 'scan_point_cloud_mesh',
    supportedLabel: '.asc point cloud and .stl mesh',
    fileFields: [
      {
        name: 'point_cloud_file',
        label: 'Point cloud file',
        allowedExtensions: ['.asc'],
      },
      {
        name: 'mesh_file',
        label: 'Mesh file',
        allowedExtensions: ['.stl'],
      },
    ],
    emptyMessage: 'Upload both a .asc point cloud file and a .stl mesh file in Thread Files before visualizing this scan card.',
    completeLabel: 'Visualize Scanned Model',
  },
};

const threadFileVisualizationCardKeys = new Set(Object.keys(threadFileVisualizationConfigs));

const statusChipColor = (status = '') => {
  if (['ready_to_start', 'completed'].includes(status)) {
    return 'success';
  }
  if (['prepare_command_sent', 'start_command_sent', 'printing', 'paused', 'pickup_pending'].includes(status)) {
    return 'warning';
  }
  if (['failed', 'cancelled'].includes(status)) {
    return 'error';
  }
  return 'default';
};

const jobFileName = (job = {}) => job.selected_file?.filename || job.selected_file?.file_name || '';

const PrintJobSummary = ({ jobs = [] }) => {
  if (!jobs.length) {
    return null;
  }

  return (
    <Stack spacing={1}>
      {jobs.slice(0, 3).map((job) => (
        <Box
          key={job.print_job_id}
          sx={{
            p: 1.25,
            borderRadius: 1.5,
            border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.72)}`,
            bgcolor: (theme) => alpha(theme.palette.background.default, 0.44),
          }}
        >
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between">
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 850, wordBreak: 'break-word' }}>
                {jobFileName(job) || job.print_job_id}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                {job.print_job_id}
              </Typography>
              {job.status_message ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
                  {job.status_message}
                </Typography>
              ) : null}
            </Box>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }}>
              <Chip size="small" color={statusChipColor(job.status)} label={formatPrintStatus(job.status)} />
              {job.progress_percent !== null && typeof job.progress_percent !== 'undefined' ? (
                <Chip size="small" variant="outlined" label={`${Number(job.progress_percent).toFixed(0)}%`} />
              ) : null}
            </Stack>
          </Stack>
        </Box>
      ))}
    </Stack>
  );
};

const getThreadFileName = (file = {}) => (
  String(file.filename || file.file_name || file.name || '').trim()
);

const getThreadFileExtension = (filename = '') => {
  const normalized = String(filename || '').trim().toLowerCase();
  const dotIndex = normalized.lastIndexOf('.');
  return dotIndex >= 0 ? normalized.slice(dotIndex) : '';
};

const threadFileMatchesExtensions = (file = {}, allowedExtensions = []) => {
  const extension = getThreadFileExtension(getThreadFileName(file));
  return allowedExtensions.map((value) => String(value || '').toLowerCase()).includes(extension);
};

const formatThreadFileSize = (value) => {
  const bytes = Number(value);
  if (!Number.isFinite(bytes) || bytes < 0) {
    return '';
  }
  if (bytes < 1024) {
    return `${bytes} bytes`;
  }
  const kilobytes = bytes / 1024;
  if (kilobytes < 1024) {
    return `${kilobytes.toFixed(kilobytes >= 10 ? 0 : 1)} KB`;
  }
  const megabytes = kilobytes / 1024;
  return `${megabytes.toFixed(megabytes >= 10 ? 1 : 2)} MB`;
};

const selectedFilePayload = (entry = {}) => ({
  field: entry.name,
  label: entry.label,
  filename: entry.filename,
  key: entry.file?.key || '',
  bucket: entry.file?.bucket || '',
  size_bytes: entry.file?.size_bytes ?? null,
  extension: getThreadFileExtension(entry.filename),
});

const ThreadFileVisualizationPreview = ({
  config,
  selectedEntries = [],
  onCreateThreadFileDownloadUrl = null,
}) => {
  const selected = selectedEntries.filter((entry) => entry.filename);

  return (
    <Stack spacing={1.25}>
      <ThreadFileRenderers
        config={config}
        selectedEntries={selected}
        onCreateFileDownloadUrl={onCreateThreadFileDownloadUrl}
      />
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 1 }}>
        <MetaBlock label="Visualization" value={config.title} />
        <MetaBlock label="Supported Formats" value={config.supportedLabel} />
        {selected.map((entry) => (
          <MetaBlock
            key={entry.name}
            label={entry.label}
            value={[
              entry.filename,
              formatThreadFileSize(entry.file?.size_bytes),
            ].filter(Boolean).join(' - ')}
          />
        ))}
      </Box>
    </Stack>
  );
};

const ThreadFileVisualizationCard = ({
  card,
  threadFiles = [],
  actionKey,
  onCompleteExchangeCard,
  onCreateThreadFileDownloadUrl,
  disabled = false,
}) => {
  const config = threadFileVisualizationConfigs[card.card_key];
  const schema = card.schema || {};
  const fileSignature = threadFiles.map((file) => getThreadFileName(file)).filter(Boolean).join('|');
  const filesByName = new Map(
    threadFiles
      .map((file) => [getThreadFileName(file), file])
      .filter(([filename]) => Boolean(filename)),
  );
  const optionsByField = (config?.fileFields || []).reduce((options, field) => ({
    ...options,
    [field.name]: threadFiles.filter((file) => threadFileMatchesExtensions(file, field.allowedExtensions)),
  }), {});
  const defaults = (config?.fileFields || []).reduce((values, field) => ({
    ...values,
    [field.name]: card.body?.[field.name] || getThreadFileName(optionsByField[field.name]?.[0]) || '',
  }), {});
  const [formValues, setFormValues] = useState(() => buildExchangeCardFormValues(schema, card.body || {}, defaults));
  const busy = actionKey === `complete:${card.card_key}`;
  const canComplete = card.can_complete && !disabled;
  const hiddenFields = (config?.fileFields || []).map((field) => field.name);
  const selectedEntries = (config?.fileFields || []).map((field) => {
    const filename = formValues[field.name] || card.body?.[field.name] || '';
    return {
      ...field,
      filename,
      file: filesByName.get(filename) || null,
    };
  });
  const allRequiredFilesSelected = selectedEntries.every((entry) => Boolean(entry.filename));
  const selectedFilesAreAvailable = selectedEntries.every((entry) => (
    !entry.filename ||
    (optionsByField[entry.name] || []).some((file) => getThreadFileName(file) === entry.filename)
  ));
  const missingFields = getMissingRequiredSchemaFields(schema, formValues);

  React.useEffect(() => {
    setFormValues(buildExchangeCardFormValues(schema, card.body || {}, defaults));
  }, [card.card_key, card.status, card.body, schema, fileSignature]);

  if (!config) {
    return <ExchangeCardContent card={card} />;
  }

  const completeCard = () => {
    const selectedFiles = selectedEntries.map(selectedFilePayload);
    const body = {
      ...sanitizeExchangeCardFormValues(schema, formValues, { includeCompletionStamp: false }),
      selected_files: selectedFiles,
      visualization_type: config.visualizationType,
      visualized_at: new Date().toISOString(),
    };
    if (selectedFiles.length === 1) {
      body.selected_file = selectedFiles[0];
    }
    config.fileFields.forEach((field) => {
      body[field.name] = formValues[field.name] || '';
    });
    onCompleteExchangeCard?.(card, body);
  };

  return (
    <Stack spacing={1.5}>
      <ExchangeCardContent
        card={card}
        showSchemaDefinitions={!canComplete}
        showSubmittedValues={!canComplete}
      >
        {canComplete ? (
          <Stack spacing={1.1}>
            {config.fileFields.map((fileField) => {
              const schemaField = getSchemaField(schema, fileField.name) || {
                name: fileField.name,
                label: fileField.label,
                required: true,
                type: 'file',
              };
              const options = optionsByField[fileField.name] || [];
              const value = formValues[fileField.name] || '';
              const hasValueInOptions = options.some((file) => getThreadFileName(file) === value);
              return (
                <TextField
                  key={fileField.name}
                  select
                  size="small"
                  label={getSchemaFieldLabel(schemaField)}
                  value={value}
                  onChange={(event) => setExchangeCardFormValue(setFormValues, schemaField, event.target.value)}
                  disabled={disabled || busy || !options.length}
                  required={Boolean(schemaField.required)}
                  helperText={`Supported: ${fileField.allowedExtensions.join(', ')}`}
                  fullWidth
                >
                  {value && !hasValueInOptions ? (
                    <MenuItem value={value} disabled>
                      {value} (not listed in Thread Files)
                    </MenuItem>
                  ) : null}
                  {options.map((file) => {
                    const filename = getThreadFileName(file);
                    return (
                      <MenuItem key={file.key || filename} value={filename}>
                        {filename}
                      </MenuItem>
                    );
                  })}
                </TextField>
              );
            })}
            <ExchangeCardBodyForm
              schema={schema}
              values={formValues}
              onChange={(field, value) => setExchangeCardFormValue(setFormValues, field, value)}
              disabled={disabled || busy}
              hiddenFields={hiddenFields}
            />
            {Object.values(optionsByField).some((options) => !options.length) ? (
              <Alert severity="info">{config.emptyMessage}</Alert>
            ) : null}
            {!selectedFilesAreAvailable ? (
              <Alert severity="warning">
                One selected file is no longer listed in Thread Files. Select an uploaded file before visualizing.
              </Alert>
            ) : null}
            <MissingRequiredFieldsAlert fields={missingFields} />
            {allRequiredFilesSelected ? (
              <ThreadFileVisualizationPreview
                config={config}
                selectedEntries={selectedEntries}
                onCreateThreadFileDownloadUrl={onCreateThreadFileDownloadUrl}
              />
            ) : null}
            <Stack direction="row" justifyContent="flex-end">
              <Button
                variant="contained"
                startIcon={<ViewInArRoundedIcon />}
                disabled={
                  disabled ||
                  busy ||
                  !allRequiredFilesSelected ||
                  !selectedFilesAreAvailable ||
                  !isExchangeCardFormComplete(schema, formValues) ||
                  !onCompleteExchangeCard
                }
                onClick={completeCard}
              >
                {busy ? 'Rendering...' : config.completeLabel}
              </Button>
            </Stack>
          </Stack>
        ) : null}
      </ExchangeCardContent>
      {(card.status === 'completed' || !canComplete) && selectedEntries.some((entry) => entry.filename) ? (
        <ThreadFileVisualizationPreview
          config={config}
          selectedEntries={selectedEntries}
          onCreateThreadFileDownloadUrl={onCreateThreadFileDownloadUrl}
        />
      ) : null}
    </Stack>
  );
};

const SlicedFileUploadPrintCard = ({
  card,
  threadFiles = [],
  printJobs = [],
  actionKey,
  onPreparePrintJob,
  disabled = false,
}) => {
  const printableFiles = threadFiles.filter(isPrintableFile);
  const schema = card.schema || {};
  const defaultFilename = card.body?.filename || printableFiles[0]?.filename || '';
  const filenameField = getSchemaField(schema, 'filename') || {
    name: 'filename',
    label: 'Sliced print file',
    required: true,
    type: 'file',
  };
  const [formValues, setFormValues] = useState(() => buildExchangeCardFormValues(schema, card.body || {}, {
    filename: defaultFilename,
    sliced_for_selected_printer: Boolean(card.body?.sliced_for_selected_printer),
    upload_confirmed: Boolean(card.body?.upload_confirmed),
  }));
  const busy = actionKey === `prepare:${card.card_key}`;
  const hasActivePrintJob = printJobs.some((job) => activePrintJobStatuses.has(String(job.status || '')));
  const canPrepare = (card.can_complete || card.status === 'completed') && !hasActivePrintJob;
  const filename = formValues.filename || '';
  const relatedJobs = printJobs.filter((job) => {
    const status = String(job.status || '');
    return jobFileName(job) === (card.body?.filename || filename) || activePrintJobStatuses.has(status);
  });
  const missingFields = getMissingRequiredSchemaFields(schema, formValues);

  React.useEffect(() => {
    setFormValues(buildExchangeCardFormValues(schema, card.body || {}, {
      filename: defaultFilename,
      sliced_for_selected_printer: Boolean(card.body?.sliced_for_selected_printer),
      upload_confirmed: Boolean(card.body?.upload_confirmed),
    }));
  }, [
    card.card_key,
    card.status,
    card.body?.filename,
    card.body?.sliced_for_selected_printer,
    card.body?.upload_confirmed,
    defaultFilename,
  ]);

  return (
    <Stack spacing={1.5}>
      <ExchangeCardContent
        card={card}
        showSchemaDefinitions={!canPrepare}
        showSubmittedValues={!canPrepare}
      >
        {canPrepare ? (
          <Stack spacing={1.1}>
            <TextField
              select
              size="small"
              label={getSchemaFieldLabel(filenameField)}
              value={filename}
              onChange={(event) => setExchangeCardFormValue(setFormValues, filenameField, event.target.value)}
              disabled={disabled || busy || !printableFiles.length}
              required={Boolean(filenameField.required)}
              helperText={filenameField.description || ' '}
              fullWidth
            >
              {printableFiles.map((file) => (
                <MenuItem key={file.key || file.filename} value={file.filename}>
                  {file.filename}
                </MenuItem>
              ))}
            </TextField>
            <ExchangeCardBodyForm
              schema={schema}
              values={formValues}
              onChange={(field, value) => setExchangeCardFormValue(setFormValues, field, value)}
              disabled={disabled || busy}
              hiddenFields={['filename']}
            />
            {!printableFiles.length ? (
              <Alert severity="info">
                Upload a .gcode or .bgcode file in Thread Files before preparing the printer.
              </Alert>
            ) : null}
            <MissingRequiredFieldsAlert fields={missingFields} />
            <Stack direction="row" justifyContent="flex-end">
              <Button
                variant="contained"
                startIcon={<LocalPrintshopRoundedIcon />}
                disabled={
                  disabled ||
                  busy ||
                  !filename ||
                  !isExchangeCardFormComplete(schema, formValues) ||
                  !onPreparePrintJob
                }
                onClick={() => onPreparePrintJob?.(card, {
                  ...sanitizeExchangeCardFormValues(schema, formValues, { includeCompletionStamp: false }),
                  filename,
                  sliced_for_selected_printer: Boolean(formValues.sliced_for_selected_printer),
                  upload_confirmed: Boolean(formValues.upload_confirmed),
                })}
              >
                {busy ? 'Preparing...' : 'Prepare Printer File'}
              </Button>
            </Stack>
          </Stack>
        ) : null}
      </ExchangeCardContent>
      <PrintJobSummary jobs={relatedJobs} />
    </Stack>
  );
};

const StartPrintJobCard = ({
  card,
  printJobs = [],
  actionKey,
  onStartPrintJob,
  disabled = false,
}) => {
  const readyJobs = printJobs.filter((job) => job.status === 'ready_to_start');
  const schema = card.schema || {};
  const defaultPrintJobId = card.body?.print_job_id || readyJobs[0]?.print_job_id || '';
  const printJobIdField = getSchemaField(schema, 'print_job_id') || {
    name: 'print_job_id',
    label: 'Ready print job',
    required: true,
    type: 'text',
  };
  const [formValues, setFormValues] = useState(() => buildExchangeCardFormValues(schema, card.body || {}, {
    print_job_id: defaultPrintJobId,
    start_confirmed: Boolean(card.body?.start_confirmed),
  }));
  const printJobId = formValues.print_job_id || '';
  const selectedJob = readyJobs.find((job) => job.print_job_id === printJobId) || readyJobs[0] || null;
  const busy = selectedJob ? actionKey === `start:${selectedJob.print_job_id}` : false;
  const missingFields = getMissingRequiredSchemaFields(schema, formValues);

  React.useEffect(() => {
    setFormValues(buildExchangeCardFormValues(schema, card.body || {}, {
      print_job_id: defaultPrintJobId,
      start_confirmed: Boolean(card.body?.start_confirmed),
    }));
  }, [card.card_key, card.status, card.body?.print_job_id, card.body?.start_confirmed, defaultPrintJobId]);

  return (
    <Stack spacing={1.5}>
      <ExchangeCardContent
        card={card}
        showSchemaDefinitions={!card.can_complete}
        showSubmittedValues={!card.can_complete}
      >
        {card.can_complete ? (
          <Stack spacing={1.1}>
            {readyJobs.length ? (
              <TextField
                select
                size="small"
                label={getSchemaFieldLabel(printJobIdField)}
                value={selectedJob?.print_job_id || ''}
                onChange={(event) => setExchangeCardFormValue(setFormValues, printJobIdField, event.target.value)}
                disabled={disabled || busy}
                required={Boolean(printJobIdField.required)}
                helperText={printJobIdField.description || ' '}
                fullWidth
              >
                {readyJobs.map((job) => (
                  <MenuItem key={job.print_job_id} value={job.print_job_id}>
                    {jobFileName(job) || job.print_job_id}
                  </MenuItem>
                ))}
              </TextField>
            ) : (
              <Alert severity="info">
                The printer has not reported a staged file ready to start yet.
              </Alert>
            )}
            <ExchangeCardBodyForm
              schema={schema}
              values={formValues}
              onChange={(field, value) => setExchangeCardFormValue(setFormValues, field, value)}
              disabled={disabled || busy}
              hiddenFields={['print_job_id']}
            />
            <MissingRequiredFieldsAlert fields={missingFields} />
            <Stack direction="row" justifyContent="flex-end">
              <Button
                variant="contained"
                startIcon={<SendRoundedIcon />}
                disabled={
                  disabled ||
                  busy ||
                  !selectedJob ||
                  !isExchangeCardFormComplete(schema, formValues) ||
                  !onStartPrintJob
                }
                onClick={() => onStartPrintJob?.(card, selectedJob)}
              >
                {busy ? 'Starting...' : 'Start Print'}
              </Button>
            </Stack>
          </Stack>
        ) : null}
      </ExchangeCardContent>
      <PrintJobSummary jobs={printJobs.filter((job) => activePrintJobStatuses.has(String(job.status || '')))} />
    </Stack>
  );
};

const PrintPickupConfirmationCard = ({
  card,
  printJobs = [],
  actionKey,
  onConfirmPrintPickup,
  disabled = false,
}) => {
  const pickupJobs = printJobs.filter((job) => ['pickup_pending', 'completed'].includes(job.status));
  const schema = card.schema || {};
  const defaultPrintJobId = card.body?.print_job_id || pickupJobs[0]?.print_job_id || '';
  const printJobIdField = getSchemaField(schema, 'print_job_id') || {
    name: 'print_job_id',
    label: 'Completed print job',
    required: true,
    type: 'text',
  };
  const [formValues, setFormValues] = useState(() => buildExchangeCardFormValues(schema, card.body || {}, {
    print_job_id: defaultPrintJobId,
    pickup_confirmed: Boolean(card.body?.pickup_confirmed),
    pickup_notes: card.body?.pickup_notes || '',
  }));
  const printJobId = formValues.print_job_id || '';
  const selectedJob = pickupJobs.find((job) => job.print_job_id === printJobId) || pickupJobs[0] || null;
  const busy = selectedJob ? actionKey === `pickup:${selectedJob.print_job_id}` : false;
  const missingFields = getMissingRequiredSchemaFields(schema, formValues);

  React.useEffect(() => {
    setFormValues(buildExchangeCardFormValues(schema, card.body || {}, {
      print_job_id: defaultPrintJobId,
      pickup_confirmed: Boolean(card.body?.pickup_confirmed),
      pickup_notes: card.body?.pickup_notes || '',
    }));
  }, [
    card.card_key,
    card.status,
    card.body?.print_job_id,
    card.body?.pickup_confirmed,
    card.body?.pickup_notes,
    defaultPrintJobId,
  ]);

  return (
    <Stack spacing={1.5}>
      <ExchangeCardContent
        card={card}
        showSchemaDefinitions={!card.can_complete}
        showSubmittedValues={!card.can_complete}
      >
        {card.can_complete ? (
          <Stack spacing={1.1}>
            {pickupJobs.length ? (
              <TextField
                select
                size="small"
                label={getSchemaFieldLabel(printJobIdField)}
                value={selectedJob?.print_job_id || ''}
                onChange={(event) => setExchangeCardFormValue(setFormValues, printJobIdField, event.target.value)}
                disabled={disabled || busy}
                required={Boolean(printJobIdField.required)}
                helperText={printJobIdField.description || ' '}
                fullWidth
              >
                {pickupJobs.map((job) => (
                  <MenuItem key={job.print_job_id} value={job.print_job_id}>
                    {jobFileName(job) || job.print_job_id}
                  </MenuItem>
                ))}
              </TextField>
            ) : (
              <Alert severity="info">
                No completed QNI print is waiting for pickup confirmation yet.
              </Alert>
            )}
            <ExchangeCardBodyForm
              schema={schema}
              values={formValues}
              onChange={(field, value) => setExchangeCardFormValue(setFormValues, field, value)}
              disabled={disabled || busy}
              hiddenFields={['print_job_id']}
            />
            <MissingRequiredFieldsAlert fields={missingFields} />
            <Stack direction="row" justifyContent="flex-end">
              <Button
                variant="contained"
                color="success"
                startIcon={<CheckCircleRoundedIcon />}
                disabled={
                  disabled ||
                  busy ||
                  !selectedJob ||
                  !isExchangeCardFormComplete(schema, formValues) ||
                  !onConfirmPrintPickup
                }
                onClick={() => onConfirmPrintPickup?.(
                  card,
                  selectedJob,
                  {
                    ...sanitizeExchangeCardFormValues(schema, formValues, { includeCompletionStamp: false }),
                    print_job_id: selectedJob.print_job_id,
                    pickup_confirmed: Boolean(formValues.pickup_confirmed),
                    pickup_notes: formValues.pickup_notes || '',
                  },
                )}
              >
                {busy ? 'Confirming...' : 'Confirm Pickup'}
              </Button>
            </Stack>
          </Stack>
        ) : null}
      </ExchangeCardContent>
      <PrintJobSummary jobs={pickupJobs} />
    </Stack>
  );
};

const ExchangeCardCompletionForm = ({
  card,
  actionKey,
  onCompleteExchangeCard,
  disabled = false,
}) => {
  const schema = card.schema || {};
  const [formValues, setFormValues] = useState(() => buildExchangeCardFormValues(schema, card.body || {}));
  const busy = actionKey === `complete:${card.card_key}`;
  const missingFields = getMissingRequiredSchemaFields(schema, formValues);

  React.useEffect(() => {
    setFormValues(buildExchangeCardFormValues(schema, card.body || {}));
  }, [card.card_key, card.status, card.body]);

  const completeCard = () => {
    onCompleteExchangeCard?.(
      card,
      sanitizeExchangeCardFormValues(schema, formValues),
    );
  };

  return (
    <ExchangeCardContent card={card} showSchemaDefinitions={false} showSubmittedValues={false}>
      <Stack spacing={1.25}>
        <ExchangeCardBodyForm
          schema={schema}
          values={formValues}
          onChange={(field, value) => setExchangeCardFormValue(setFormValues, field, value)}
          disabled={disabled || busy}
        />
        <MissingRequiredFieldsAlert fields={missingFields} />
        <Stack direction="row" justifyContent="flex-end">
          <Button
            variant="contained"
            color="success"
            startIcon={<CheckCircleRoundedIcon />}
            disabled={
              disabled ||
              busy ||
              !isExchangeCardFormComplete(schema, formValues) ||
              !onCompleteExchangeCard
            }
            onClick={completeCard}
          >
            {busy ? 'Confirming...' : 'Confirm Complete'}
          </Button>
        </Stack>
      </Stack>
    </ExchangeCardContent>
  );
};

const ExchangeTimelineCard = ({
  card,
  actionKey,
  printActionKey,
  onCompleteExchangeCard,
  onCreateThreadFileDownloadUrl,
  onPublishExchangeCard,
  onRemoveExchangeCard,
  onShareExchangeCard,
  onEditExchangeCard,
  onExportExchangeCard,
  onViewExchangeCardRevisions,
  onPreparePrintJob,
  onStartPrintJob,
  onConfirmPrintPickup,
  onSendEddieMessage,
  onStartInitialRequest,
  threadFiles = [],
  printJobs = [],
  managedAssets = [],
  managedAssetsLoading = false,
  disabled = false,
}) => {
  const cardActionBusy = Boolean(actionKey && actionKey.endsWith(`:${card.card_key}`));
  const isComplete = card.status === 'completed';
  const isDraft = card.status === 'draft';
  const isInitialRequestCard = card.card_key === 'initial_request';
  const isAttachManagedAsset = card.card_key === 'attach_managed_asset';
  const isEddieAssistance = card.card_key === 'eddie_assistance';
  const isSlicedFileUpload = card.card_key === 'sliced_file_upload';
  const isStartPrintJob = card.card_key === 'start_print_job';
  const isPickupConfirmation = card.card_key === 'print_pickup_confirmation';
  const isXTdpCard = card.card_key === 'xtdp';
  const isViewTelemetry = telemetryExchangeCardKeys.has(card.card_key);
  const isThreadFileVisualization = threadFileVisualizationCardKeys.has(card.card_key);
  const isSystemPrintEvent = systemPrintEventCards.has(card.card_key);

  return (
    <Stack spacing={1.5}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between">
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
          <Chip
            size="small"
            color={isComplete ? 'success' : isDraft ? 'warning' : 'primary'}
            icon={isComplete ? <CheckCircleRoundedIcon /> : undefined}
            label={getStatusLabel(card.status)}
          />
          {card.is_required ? <Chip size="small" variant="outlined" color="primary" label="Required" /> : null}
          {card.blocked ? <Chip size="small" variant="outlined" color="warning" label="Blocked" /> : null}
          {card.revision_count ? <Chip size="small" variant="outlined" icon={<HistoryRoundedIcon />} label={`${card.revision_count} edit${card.revision_count === 1 ? '' : 's'}`} /> : null}
        </Stack>
        <ExchangeCardToolbar
          card={card}
          busy={disabled || cardActionBusy}
          onPublish={onPublishExchangeCard}
          onRemove={onRemoveExchangeCard}
          onShare={onShareExchangeCard}
          onEdit={onEditExchangeCard}
          onExport={onExportExchangeCard}
          onHistory={onViewExchangeCardRevisions}
        />
      </Stack>
      {card.blocked ? <Alert severity="info">{card.blocked_reason}</Alert> : null}
      {isAttachManagedAsset ? (
        <ManagedAssetAttachment
          card={card}
          actionKey={actionKey}
          managedAssets={managedAssets}
          managedAssetsLoading={managedAssetsLoading}
          onCompleteExchangeCard={onCompleteExchangeCard}
          disabled={disabled}
        />
      ) : isEddieAssistance ? (
        <EddieAssistanceCard
          card={card}
          actionKey={actionKey}
          onSendEddieMessage={onSendEddieMessage}
          disabled={disabled}
        />
      ) : isSlicedFileUpload ? (
        <SlicedFileUploadPrintCard
          card={card}
          threadFiles={threadFiles}
          printJobs={printJobs}
          actionKey={printActionKey}
          onPreparePrintJob={onPreparePrintJob}
          disabled={disabled}
        />
      ) : isStartPrintJob ? (
        <StartPrintJobCard
          card={card}
          printJobs={printJobs}
          actionKey={printActionKey}
          onStartPrintJob={onStartPrintJob}
          disabled={disabled}
        />
      ) : isPickupConfirmation ? (
        <PrintPickupConfirmationCard
          card={card}
          printJobs={printJobs}
          actionKey={printActionKey}
          onConfirmPrintPickup={onConfirmPrintPickup}
          disabled={disabled}
        />
      ) : isXTdpCard ? (
        <XTdpAttachmentCard card={card} />
      ) : isViewTelemetry ? (
        <ViewTelemetryCard card={card} />
      ) : isThreadFileVisualization ? (
        <ThreadFileVisualizationCard
          card={card}
          threadFiles={threadFiles}
          actionKey={actionKey}
          onCompleteExchangeCard={onCompleteExchangeCard}
          onCreateThreadFileDownloadUrl={onCreateThreadFileDownloadUrl}
          disabled={disabled}
        />
      ) : !isInitialRequestCard && !isSystemPrintEvent && card.can_complete ? (
        <ExchangeCardCompletionForm
          card={card}
          actionKey={actionKey}
          onCompleteExchangeCard={onCompleteExchangeCard}
          disabled={disabled}
        />
      ) : (
        <ExchangeCardContent card={card} />
      )}
      {isInitialRequestCard && !isComplete && onStartInitialRequest ? (
        <Stack direction="row" justifyContent="flex-end">
          <Button
            variant="contained"
            startIcon={<EditRoundedIcon />}
            disabled={disabled}
            onClick={() => onStartInitialRequest?.(card)}
          >
            Fill Request
          </Button>
        </Stack>
      ) : null}
    </Stack>
  );
};

const MessageListCard = ({ card }) => {
  const messages = card.confirmations || card.notifications || card.updates || card.files || card.telemetry || card.errors || [];
  return (
    <Stack spacing={1.25}>
      {messages.map((message) => (
        <Box
          key={message.message_uuid}
          sx={{
            borderRadius: 1.5,
            border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.68)}`,
            p: 1.25,
          }}
        >
          <Stack spacing={1}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 1 }}>
              <MetaBlock label="Sent" value={formatDateTime(message.created_at)} />
              <MetaBlock
                label="Partner"
                value={message.sender_partner ? getPartnerDisplayName(message.sender_partner) : ''}
              />
            </Box>
            <SchemaBodyFields body={message.body || {}} compact />
          </Stack>
        </Box>
      ))}
    </Stack>
  );
};

const getActorAtPartnerLabel = (actor = {}) => {
  const user = actor.user?.user_email || actor.display_name || actor.user?.user_full_name || actor.user?.user_id || 'User';
  const partner = actor.partner?.display_name || actor.partner?.legal_name || actor.partner?.partner_code || '';
  return partner ? `${user}@${partner}` : user;
};

const getFileEventVerb = (message = {}) => {
  const subtype = String(message.message_subtype || '').toLowerCase();
  if (subtype === 'delete' || subtype === 'deleted') {
    return 'deleted file';
  }
  if (subtype === 'update' || subtype === 'updated') {
    return 'uploaded new version of file';
  }
  return 'uploaded file';
};

const FileTimelineMessage = ({ message }) => {
  const body = message.body || {};
  const filename = body.filename || body.file_name || body.name || 'Untitled file';
  const actor = getActorAtPartnerLabel(message.added_by || {});

  return (
    <Box
      sx={{
        borderRadius: 1.5,
        border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.72)}`,
        bgcolor: (theme) => alpha(theme.palette.background.default, 0.58),
        px: { xs: 1.25, md: 1.5 },
        py: 1.15,
      }}
    >
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={0.75} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between">
        <Typography variant="body2" sx={{ fontWeight: 800, wordBreak: 'break-word' }}>
          {actor} {getFileEventVerb(message)}: {filename}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
          {formatDateTime(message.created_at)}
        </Typography>
      </Stack>
    </Box>
  );
};

const telemetryColors = ['#2563eb', '#16a34a', '#f97316', '#7c3aed', '#dc2626', '#0891b2'];

const TELEMETRY_HOUR_OPTIONS = Array.from({ length: 24 }, (_, hour) => ({
  value: String(hour).padStart(2, '0'),
  label: new Date(2020, 0, 1, hour).toLocaleTimeString([], { hour: 'numeric' }),
}));

const formatTelemetryDateInput = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return '';
  }
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60 * 1000);
  return local.toISOString().slice(0, 10);
};

const normalizeTelemetryHour = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return '00';
  }
  return String(Math.min(23, Math.max(0, Math.trunc(parsed)))).padStart(2, '0');
};

const normalizeTelemetryRangePart = (part = {}) => ({
  date: String(part.date || '').trim(),
  hour: normalizeTelemetryHour(part.hour),
});

const normalizeTelemetryRange = (range = {}) => ({
  from: normalizeTelemetryRangePart(range.from),
  to: normalizeTelemetryRangePart(range.to),
});

const telemetryRangeHoursToWindow = (rangeHours) => {
  const to = new Date();
  to.setMinutes(0, 0, 0);
  const hours = Math.max(1, Math.trunc(Number(rangeHours) || 1));
  const from = new Date(to.getTime() - hours * 60 * 60 * 1000);
  return normalizeTelemetryRange({
    from: {
      date: formatTelemetryDateInput(from),
      hour: from.getHours(),
    },
    to: {
      date: formatTelemetryDateInput(to),
      hour: to.getHours(),
    },
  });
};

const telemetryRangePartToDate = (part, label) => {
  const normalized = normalizeTelemetryRangePart(part);
  const match = normalized.date.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) {
    throw new Error(`Select a valid ${label} date.`);
  }
  const [, yearText, monthText, dayText] = match;
  const year = Number(yearText);
  const month = Number(monthText);
  const day = Number(dayText);
  const hour = Number(normalized.hour);
  const date = new Date(year, month - 1, day, hour, 0, 0, 0);
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day ||
    date.getHours() !== hour
  ) {
    throw new Error(`Select a valid ${label} date.`);
  }
  return date;
};

const telemetryRangeToDates = (range) => {
  const normalized = normalizeTelemetryRange(range);
  const from = telemetryRangePartToDate(normalized.from, 'from');
  const to = telemetryRangePartToDate(normalized.to, 'to');
  if (from >= to) {
    throw new Error('From must be before To.');
  }
  return { from: from.toISOString(), to: to.toISOString() };
};

const formatTelemetryLabel = (value) => (
  String(value || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase())
);

const formatTelemetryValue = (value) => {
  if (value === null || typeof value === 'undefined' || value === '') {
    return 'N/A';
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  if (typeof value === 'number') {
    return Number.isInteger(value) ? String(value) : value.toLocaleString(undefined, { maximumFractionDigits: 4 });
  }
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }
  return String(value);
};

const formatTelemetryTick = (value) => {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const coerceTelemetryNumber = (value) => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  if (!trimmed || trimmed.toLowerCase() === 'nan') {
    return null;
  }
  const normalized = trimmed.replace(/,/g, '');
  if (!/^[-+]?(?:\d+\.?\d*|\.\d+)(?:e[-+]?\d+)?$/i.test(normalized)) {
    return null;
  }
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

const normalizeTelemetryFields = (value) => {
  const raw = Array.isArray(value)
    ? value
    : String(value || '').split(',');
  return raw.map((item) => String(item || '').trim()).filter(Boolean);
};

const getTelemetryCardAsset = (card = {}) => (
  card.asset ||
  card.metadata?.selected_asset ||
  card.body?.selected_asset ||
  {}
);

const ViewTelemetryCard = ({ card }) => {
  const body = card.body || {};
  const asset = getTelemetryCardAsset(card);
  const assetId = asset.asset_id || body.asset_id || '';
  const defaultRangeHours = String(body.default_range_hours || card.metadata?.default_range_hours || '1');
  const defaultTelemetryRange = useMemo(
    () => telemetryRangeHoursToWindow(defaultRangeHours),
    [defaultRangeHours],
  );
  const defaultFields = useMemo(
    () => normalizeTelemetryFields(body.default_fields || card.metadata?.default_fields),
    [body.default_fields, card.metadata?.default_fields],
  );
  const [telemetryRange, setTelemetryRange] = useState(defaultTelemetryRange);
  const [selectedFields, setSelectedFields] = useState(defaultFields);
  const [fieldSelectionInitialized, setFieldSelectionInitialized] = useState(Boolean(defaultFields.length));
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [state, setState] = useState({ loading: Boolean(assetId), error: '', data: null });

  const loadTelemetry = React.useCallback(async (nextRange = defaultTelemetryRange, options = {}) => {
    if (!assetId) {
      setState({ loading: false, error: '', data: null });
      return;
    }
    if (!options.silent) {
      setState((current) => ({ ...current, loading: true, error: '' }));
    }
    let from;
    let to;
    try {
      const rangeDates = telemetryRangeToDates(nextRange);
      from = rangeDates.from;
      to = rangeDates.to;
    } catch (error) {
      setState((current) => ({
        loading: false,
        error: error.message || 'Select a valid telemetry range.',
        data: options.silent ? current.data : null,
      }));
      return;
    }
    try {
      const response = await axiosInstance.get(`/assets/${encodeURIComponent(assetId)}/telemetry`, {
        params: {
          from,
          to,
          limit: 5000,
        },
      });
      setState({ loading: false, error: '', data: response.data });
      setLastUpdated(new Date());
    } catch (error) {
      setState((current) => ({
        loading: false,
        error: error?.response?.data?.message || 'Unable to query telemetry for this asset.',
        data: options.silent ? current.data : null,
      }));
    }
  }, [assetId, defaultTelemetryRange]);

  React.useEffect(() => {
    setTelemetryRange(defaultTelemetryRange);
    setSelectedFields(defaultFields);
    setFieldSelectionInitialized(Boolean(defaultFields.length));
  }, [card.card_key, card.status, defaultTelemetryRange, defaultFields]);

  React.useEffect(() => {
    loadTelemetry(defaultTelemetryRange);
  }, [assetId, defaultTelemetryRange, loadTelemetry]);

  React.useEffect(() => {
    if (!autoRefresh || !assetId) {
      return undefined;
    }
    const timer = window.setInterval(() => {
      loadTelemetry(telemetryRange, { silent: true });
    }, 15000);
    return () => window.clearInterval(timer);
  }, [autoRefresh, assetId, telemetryRange, loadTelemetry]);

  const telemetry = state.data?.telemetry;
  const rawRows = telemetry?.rows || [];
  const normalizedRows = useMemo(() => (
    rawRows
      .filter((row) => row?.field)
      .map((row) => {
        const numericValue = coerceTelemetryNumber(row.value);
        return {
          ...row,
          field: String(row.field),
          numericValue,
          isNumeric: numericValue !== null,
        };
      })
  ), [rawRows]);

  const availableFields = useMemo(() => {
    const responseFields = Array.isArray(telemetry?.fields) ? telemetry.fields : [];
    const rowFields = normalizedRows.map((row) => row.field);
    return [...new Set([...responseFields, ...rowFields])].sort();
  }, [telemetry, normalizedRows]);

  const allNumericFields = useMemo(() => (
    [...new Set(normalizedRows.filter((row) => row.isNumeric).map((row) => row.field))].sort()
  ), [normalizedRows]);

  React.useEffect(() => {
    if (fieldSelectionInitialized) {
      return;
    }
    const nextFields = defaultFields.length ? defaultFields : allNumericFields.slice(0, 6);
    if (!nextFields.length) {
      return;
    }
    setSelectedFields(nextFields);
    setFieldSelectionInitialized(true);
  }, [allNumericFields, defaultFields, fieldSelectionInitialized]);

  const visibleRows = useMemo(() => (
    normalizedRows.filter((row) => selectedFields.includes(row.field))
  ), [normalizedRows, selectedFields]);

  const numericFields = useMemo(() => (
    [...new Set(visibleRows.filter((row) => row.isNumeric).map((row) => row.field))].sort()
  ), [visibleRows]);

  const latestByField = useMemo(() => {
    const latest = new Map();
    visibleRows.forEach((row) => latest.set(row.field, row));
    return latest;
  }, [visibleRows]);

  const chartData = useMemo(() => {
    const byTime = new Map();
    visibleRows
      .filter((row) => row.isNumeric)
      .forEach((row) => {
        const item = byTime.get(row.time) || { time: row.time };
        item[row.field] = row.numericValue;
        byTime.set(row.time, item);
      });
    return Array.from(byTime.values()).sort((left, right) => new Date(left.time) - new Date(right.time));
  }, [visibleRows]);

  const changeRangePart = (rangeKey, fieldKey) => (event) => {
    const nextRange = normalizeTelemetryRange({
      ...telemetryRange,
      [rangeKey]: {
        ...(telemetryRange[rangeKey] || {}),
        [fieldKey]: event.target.value,
      },
    });
    setTelemetryRange(nextRange);
    loadTelemetry(nextRange);
  };

  const changeFields = (event) => {
    const value = event.target.value;
    const nextFields = typeof value === 'string' ? value.split(',') : value;
    setFieldSelectionInitialized(true);
    setSelectedFields(nextFields);
  };

  const removeTelemetryField = (field) => {
    const nextFields = selectedFields.filter((selectedField) => selectedField !== field);
    setFieldSelectionInitialized(true);
    setSelectedFields(nextFields);
  };

  if (!assetId) {
    return (
      <Alert severity="info">
        Attach a managed asset before viewing telemetry in this Exchange Card.
      </Alert>
    );
  }

  return (
    <Stack spacing={1.5}>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }, gap: 1 }}>
        <MetaBlock label="Asset" value={asset.asset_name || asset.source_capability_asset_id || assetId} />
        <MetaBlock label="Telemetry Fields" value={numericFields.length ? `${numericFields.length} numeric` : 'No numeric fields'} />
        <MetaBlock label="Last Updated" value={lastUpdated ? lastUpdated.toLocaleTimeString() : 'N/A'} />
      </Box>

      <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.25} alignItems={{ xs: 'stretch', lg: 'center' }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: '160px 112px 160px 112px' },
            gap: 1,
          }}
        >
          <TextField
            size="small"
            type="date"
            label="From Date"
            value={telemetryRange.from.date}
            onChange={changeRangePart('from', 'date')}
            InputLabelProps={{ shrink: true }}
          />
          <FormControl size="small">
            <InputLabel id={`${card.card_key}-telemetry-from-hour-label`}>From Hour</InputLabel>
            <Select
              labelId={`${card.card_key}-telemetry-from-hour-label`}
              label="From Hour"
              value={telemetryRange.from.hour}
              onChange={changeRangePart('from', 'hour')}
            >
              {TELEMETRY_HOUR_OPTIONS.map((option) => (
                <MenuItem key={`from-${option.value}`} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            size="small"
            type="date"
            label="To Date"
            value={telemetryRange.to.date}
            onChange={changeRangePart('to', 'date')}
            InputLabelProps={{ shrink: true }}
          />
          <FormControl size="small">
            <InputLabel id={`${card.card_key}-telemetry-to-hour-label`}>To Hour</InputLabel>
            <Select
              labelId={`${card.card_key}-telemetry-to-hour-label`}
              label="To Hour"
              value={telemetryRange.to.hour}
              onChange={changeRangePart('to', 'hour')}
            >
              {TELEMETRY_HOUR_OPTIONS.map((option) => (
                <MenuItem key={`to-${option.value}`} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <FormControl size="small" sx={{ flex: 1, minWidth: { xs: '100%', lg: 220 } }}>
          <InputLabel id={`${card.card_key}-telemetry-fields-label`}>Fields</InputLabel>
          <Select
            labelId={`${card.card_key}-telemetry-fields-label`}
            multiple
            label="Fields"
            value={selectedFields}
            input={<OutlinedInput label="Fields" />}
            onChange={changeFields}
            renderValue={(selected) => selected.length ? selected.map(formatTelemetryLabel).join(', ') : 'Select fields'}
          >
            {availableFields.map((field) => (
              <MenuItem key={field} value={field}>
                <Checkbox checked={selectedFields.includes(field)} />
                <ListItemText primary={formatTelemetryLabel(field)} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControlLabel
          control={<Switch checked={autoRefresh} onChange={(event) => setAutoRefresh(event.target.checked)} />}
          label="Auto refresh"
          sx={{ m: 0, minWidth: 140 }}
        />
        <Button variant="outlined" onClick={() => loadTelemetry(telemetryRange)} disabled={state.loading}>
          Refresh
        </Button>
      </Stack>

      {state.loading ? <LinearProgress /> : null}
      {state.error ? <Alert severity="error">{state.error}</Alert> : null}
      {!state.loading && !state.error && telemetry && !telemetry.configured ? (
        <Alert severity="info">{telemetry.message || 'Telemetry is not configured yet.'}</Alert>
      ) : null}

      {!state.loading && !state.error && telemetry?.configured ? (
        <Stack spacing={1.5}>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
            {numericFields.map((field, index) => {
              const row = latestByField.get(field);
              return (
                <Chip
                  key={field}
                  label={`${formatTelemetryLabel(field)}: ${formatTelemetryValue(row?.numericValue)}`}
                  variant="outlined"
                  onClick={() => removeTelemetryField(field)}
                  onDelete={() => removeTelemetryField(field)}
                  sx={{
                    borderColor: telemetryColors[index % telemetryColors.length],
                    color: telemetryColors[index % telemetryColors.length],
                    fontWeight: 750,
                    cursor: 'pointer',
                  }}
                />
              );
            })}
          </Stack>

          {chartData.length && numericFields.length ? (
            <Box sx={{ height: { xs: 320, md: 420 }, width: '100%' }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 12, right: 24, left: 0, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#d6dde8" />
                  <XAxis dataKey="time" tickFormatter={formatTelemetryTick} minTickGap={24} tick={{ fontSize: 12 }} />
                  <YAxis width={56} tick={{ fontSize: 12 }} />
                  <RechartsTooltip
                    labelFormatter={(label) => formatDateTime(label)}
                    formatter={(value, name) => [formatTelemetryValue(value), formatTelemetryLabel(name)]}
                  />
                  <Legend formatter={formatTelemetryLabel} />
                  {numericFields.map((field, index) => (
                    <Line
                      key={field}
                      type="monotone"
                      dataKey={field}
                      name={field}
                      stroke={telemetryColors[index % telemetryColors.length]}
                      strokeWidth={2.35}
                      dot={chartData.length <= 20}
                      activeDot={{ r: 5 }}
                      connectNulls
                      isAnimationActive={false}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </Box>
          ) : (
            <Alert severity="info">
              No numeric telemetry was returned for this asset and time range.
            </Alert>
          )}
        </Stack>
      ) : null}
    </Stack>
  );
};

const MachineSelectionCard = ({ card }) => {
  const machine = card.machine || {};
  const plan = card.plan || [];

  return (
    <Stack spacing={1.5}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 0.9fr) minmax(0, 1.1fr)' },
          gap: 1.5,
        }}
      >
        <Box
          sx={{
            p: 1.5,
            borderRadius: 1.5,
            border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.72)}`,
            bgcolor: (theme) => alpha(theme.palette.info.main, 0.035),
          }}
        >
          <Stack spacing={1.25}>
            <Stack direction="row" spacing={1} alignItems="center">
              <BuildCircleRoundedIcon color="primary" fontSize="small" />
              <Typography variant="subtitle2" sx={{ fontWeight: 850 }}>
                {machine.name || 'Selected Machine'}
              </Typography>
            </Stack>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                gap: 1,
              }}
            >
              <MetaBlock label="Builder" value={machine.builder} />
              <MetaBlock label="Controller" value={machine.controller} />
              <MetaBlock label="Location" value={machine.location} />
              <MetaBlock label="Telemetry" value={machine.telemetry} />
            </Box>
          </Stack>
        </Box>

        <Stack spacing={1}>
          {plan.map((step) => (
            <Box
              key={step.label}
              sx={{
                p: 1.25,
                borderRadius: 1.5,
                border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.68)}`,
              }}
            >
              <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
                <Typography variant="body2" sx={{ fontWeight: 800 }}>
                  {step.label}
                </Typography>
                <Chip size="small" color={step.status === 'Complete' ? 'success' : 'primary'} label={step.status} />
              </Stack>
              <LinearProgress
                variant="determinate"
                value={step.progress ?? 0}
                sx={{ mt: 1, height: 6, borderRadius: 999 }}
              />
            </Box>
          ))}
        </Stack>
      </Box>
      <SchemaBodyFields body={card.body || {}} compact />
    </Stack>
  );
};

const MachineTelemetryCard = ({ card }) => {
  const series = card.series || [];
  const channels = card.channels || [];
  const metrics = card.metrics || [];

  return (
    <Stack spacing={2}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' },
          gap: 1,
        }}
      >
        {metrics.map((metric) => (
          <Box
            key={metric.label}
            sx={{
              p: 1.25,
              borderRadius: 1.5,
              border: (theme) => `1px solid ${alpha(metric.color || theme.palette.primary.main, 0.32)}`,
              bgcolor: (theme) => alpha(metric.color || theme.palette.primary.main, 0.055),
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
              {metric.label}
            </Typography>
            <Typography variant="h6" sx={{ mt: 0.25, fontWeight: 900 }}>
              {metric.value}
            </Typography>
          </Box>
        ))}
      </Box>

      <Box sx={{ height: { xs: 340, md: 420 }, width: '100%' }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={series} margin={{ top: 12, right: 24, left: 4, bottom: 8 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#d6dde8" />
            <XAxis dataKey="time" minTickGap={22} tick={{ fontSize: 12 }} />
            <YAxis domain={[0, 100]} width={42} tick={{ fontSize: 12 }} />
            <RechartsTooltip
              formatter={(value, name) => [`${Number(value).toFixed(1)}%`, name]}
              labelFormatter={(label) => `Process time ${label}`}
            />
            <Legend />
            {channels.map((channel, index) => (
              <Line
                key={channel.key}
                type="monotone"
                dataKey={channel.key}
                name={channel.label}
                stroke={channel.color || telemetryColors[index % telemetryColors.length]}
                strokeWidth={2.4}
                dot={false}
                activeDot={{ r: 5 }}
                isAnimationActive={false}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </Box>

      <SchemaBodyFields body={card.body || {}} compact />
    </Stack>
  );
};

const ExpertRecommendationsCard = ({ card }) => {
  const recommendations = card.recommendations || [];
  const actionItems = card.action_items || [];

  return (
    <Stack spacing={1.5}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
          gap: 1,
        }}
      >
        <MetaBlock label="Expert" value={card.expert_name} />
        <MetaBlock label="Discipline" value={card.discipline} />
        <MetaBlock label="Source Thread" value={card.source_corr_id} />
      </Box>

      <Stack spacing={1}>
        {recommendations.map((recommendation, index) => (
          <Box
            key={recommendation.title}
            sx={{
              p: 1.4,
              borderRadius: 1.5,
              border: (theme) => `1px solid ${alpha(theme.palette.success.main, 0.24)}`,
              bgcolor: (theme) => alpha(theme.palette.success.main, 0.045),
            }}
          >
            <Stack direction="row" spacing={1.25} alignItems="flex-start">
              <FactCheckRoundedIcon color="success" fontSize="small" sx={{ mt: 0.2 }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 850 }}>
                  {index + 1}. {recommendation.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.4 }}>
                  {recommendation.detail}
                </Typography>
              </Box>
            </Stack>
          </Box>
        ))}
      </Stack>

      {actionItems.length ? (
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
          {actionItems.map((item) => (
            <Chip key={item} size="small" variant="outlined" color="primary" label={item} />
          ))}
        </Stack>
      ) : null}
    </Stack>
  );
};

const PartModelCard = ({ card }) => (
  <Stack spacing={1.5}>
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1.2fr) minmax(240px, 0.8fr)' },
        gap: 1.5,
        alignItems: 'stretch',
      }}
    >
      <Suspense
        fallback={(
          <Box
            sx={{
              height: { xs: 320, md: 420 },
              borderRadius: 1.5,
              bgcolor: '#f8fafc',
              display: 'grid',
              placeItems: 'center',
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Loading part model...
            </Typography>
          </Box>
        )}
      >
        <PartModelViewer />
      </Suspense>
      <Stack spacing={1}>
        <SchemaBodyFields body={card.body || {}} compact />
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
          {(card.features || []).map((feature) => (
            <Chip key={feature} size="small" variant="outlined" label={feature} />
          ))}
        </Stack>
      </Stack>
    </Box>
  </Stack>
);

const ExchangeCardEditDialog = ({ card, open, saving, onClose, onSave }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [bodyText, setBodyText] = useState('{}');
  const [error, setError] = useState('');

  React.useEffect(() => {
    if (!open || !card) {
      return;
    }
    setTitle(card.title || '');
    setDescription(card.description || '');
    setBodyText(JSON.stringify(card.body || {}, null, 2));
    setError('');
  }, [card, open]);

  const save = () => {
    let body;
    try {
      body = JSON.parse(bodyText || '{}');
    } catch {
      setError('Card values must be valid JSON.');
      return;
    }
    if (!body || typeof body !== 'object' || Array.isArray(body)) {
      setError('Card values must be a JSON object.');
      return;
    }
    onSave?.(card, {
      title,
      description,
      body,
      replace_body: true,
    });
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle>Edit Exchange Card</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {error ? <Alert severity="error">{error}</Alert> : null}
          <TextField label="Title" value={title} onChange={(event) => setTitle(event.target.value)} fullWidth />
          <TextField
            label="Description"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            fullWidth
            multiline
            minRows={2}
          />
          <TextField
            label="Submitted values"
            value={bodyText}
            onChange={(event) => setBodyText(event.target.value)}
            fullWidth
            multiline
            minRows={8}
            sx={{ fontFamily: 'monospace' }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={save} disabled={saving}>
          {saving ? 'Saving...' : 'Save Edit'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ExchangeCardRevisionDialog = ({ card, open, loading, revisions = [], onClose }) => (
  <Dialog open={open} onClose={loading ? undefined : onClose} fullWidth maxWidth="md">
    <DialogTitle>Edit History</DialogTitle>
    <DialogContent dividers>
      <Stack spacing={1.25}>
        {loading ? <LinearProgress /> : null}
        {!loading && !revisions.length ? (
          <Typography variant="body2" color="text.secondary">
            No edits have been recorded for this card.
          </Typography>
        ) : null}
        {revisions.map((revision) => (
          <Box
            key={revision.revision_id}
            sx={{
              p: 1.25,
              borderRadius: 1.5,
              border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.72)}`,
            }}
          >
            <Stack spacing={0.75}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} justifyContent="space-between">
                <Typography variant="subtitle2" sx={{ fontWeight: 850 }}>
                  Revision {revision.revision_number}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatDateTime(revision.created_at)}
                </Typography>
              </Stack>
              <Typography variant="caption" color="text.secondary">
                {getActorLabel(revision.edited_by)}
              </Typography>
              {revision.edit_reason ? (
                <Typography variant="body2" color="text.secondary">
                  {revision.edit_reason}
                </Typography>
              ) : null}
              <SchemaBodyFields body={revision.body || {}} compact />
            </Stack>
          </Box>
        ))}
      </Stack>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose} disabled={loading}>
        Close
      </Button>
    </DialogActions>
  </Dialog>
);

const cardRendererRegistry = {
  thread: {
    machine_selection: MachineSelectionCard,
    machine_telemetry: MachineTelemetryCard,
    expert_recommendations: ExpertRecommendationsCard,
    part_model: PartModelCard,
  },
};

const renderSpecialCard = ({
  cardKey,
  card,
  exchangeCardActionKey,
  printActionKey,
  onPullExchangeCard,
  onCompleteExchangeCard,
  onCreateThreadFileDownloadUrl,
  onPublishExchangeCard,
  onRemoveExchangeCard,
  onShareExchangeCard,
  onEditExchangeCard,
  onExportExchangeCard,
  onViewExchangeCardRevisions,
  onPreparePrintJob,
  onStartPrintJob,
  onConfirmPrintPickup,
  onSendEddieMessage,
  onStartInitialRequest,
  threadFiles,
  printJobs,
  managedAssets,
  managedAssetsLoading,
  actionsDisabled,
}) => {
  if (cardKey === 'available_exchange_cards') {
    return (
      <AvailableExchangeCardsCard
        card={card}
        actionKey={exchangeCardActionKey}
        onPullExchangeCard={onPullExchangeCard}
        disabled={actionsDisabled}
      />
    );
  }

  if (card?.card_kind === 'exchange_card') {
    return (
      <ExchangeTimelineCard
        card={card}
        actionKey={exchangeCardActionKey}
        printActionKey={printActionKey}
        onCompleteExchangeCard={onCompleteExchangeCard}
        onCreateThreadFileDownloadUrl={onCreateThreadFileDownloadUrl}
        onPublishExchangeCard={onPublishExchangeCard}
        onRemoveExchangeCard={onRemoveExchangeCard}
        onShareExchangeCard={onShareExchangeCard}
        onEditExchangeCard={onEditExchangeCard}
        onExportExchangeCard={onExportExchangeCard}
        onViewExchangeCardRevisions={onViewExchangeCardRevisions}
        onPreparePrintJob={onPreparePrintJob}
        onStartPrintJob={onStartPrintJob}
        onConfirmPrintPickup={onConfirmPrintPickup}
        onSendEddieMessage={onSendEddieMessage}
        onStartInitialRequest={onStartInitialRequest}
        threadFiles={threadFiles}
        printJobs={printJobs}
        managedAssets={managedAssets}
        managedAssetsLoading={managedAssetsLoading}
        disabled={actionsDisabled}
      />
    );
  }

  if (card?.telemetry) {
    return <MessageListCard card={card} />;
  }

  const RegisteredRenderer = cardRendererRegistry.thread[cardKey];
  if (RegisteredRenderer) {
    return <RegisteredRenderer card={card} />;
  }

  return null;
};

const timelineTimestampValue = (value) => {
  if (!value) {
    return null;
  }
  const timestamp = value instanceof Date ? value.getTime() : new Date(value).getTime();
  return Number.isNaN(timestamp) ? null : timestamp;
};

const compareTimelineItems = (left, right) => {
  const leftTimestamp = timelineTimestampValue(left.createdAt);
  const rightTimestamp = timelineTimestampValue(right.createdAt);
  if (leftTimestamp !== null && rightTimestamp !== null && leftTimestamp !== rightTimestamp) {
    return leftTimestamp - rightTimestamp;
  }
  if (leftTimestamp !== null && rightTimestamp === null) {
    return -1;
  }
  if (leftTimestamp === null && rightTimestamp !== null) {
    return 1;
  }
  return (
    (left.order - right.order) ||
    String(left.key).localeCompare(String(right.key))
  );
};

export default function ThreadCardsView({
  thread,
  filesPanel = null,
  threadFiles = [],
  tdpInventory = [],
  tdpInventoryLoading = false,
  onSelectCapability = null,
  onSelectAsset = null,
  onPullExchangeCard = null,
  onCompleteExchangeCard = null,
  onCreateThreadFileDownloadUrl = null,
  onPublishExchangeCard = null,
  onRemoveExchangeCard = null,
  onShareExchangeCard = null,
  onEditExchangeCard = null,
  onExportExchangeCard = null,
  onViewExchangeCardRevisions = null,
  onPreparePrintJob = null,
  onStartPrintJob = null,
  onConfirmPrintPickup = null,
  onSendEddieMessage = null,
  onStartInitialRequest = null,
  managedAssets = [],
  managedAssetsLoading = false,
  actionsDisabled = false,
}) {
  const [selecting, setSelecting] = useState(false);
  const [showSelectedOffers, setShowSelectedOffers] = useState(false);
  const [exchangeCardActionKey, setExchangeCardActionKey] = useState('');
  const [editingCard, setEditingCard] = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  const [historyCard, setHistoryCard] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyRevisions, setHistoryRevisions] = useState([]);
  const [printActionKey, setPrintActionKey] = useState('');
  const [draggedExchangeCardKey, setDraggedExchangeCardKey] = useState('');
  const [timelineDropActive, setTimelineDropActive] = useState(false);
  const [pendingXTdpCard, setPendingXTdpCard] = useState(null);
  const [filesDialogOpen, setFilesDialogOpen] = useState(false);
  const [stepExpanded, setStepExpanded] = useState({});

  const toggleStepExpanded = (key) => {
    setStepExpanded((prev) => ({
      ...prev,
      [key]: !(prev[key] ?? true), 
    }));
  };

  const isStepExpanded = (key) => stepExpanded[key] ?? true;

  const cards = thread?.thread_cards || {};
  const printJobs = Array.isArray(thread?.print_jobs) ? thread.print_jobs : [];
  const availableExchangeCards = cards.available_exchange_cards || null;
  const availableExchangeCardItems = Array.isArray(availableExchangeCards?.cards) ? availableExchangeCards.cards : [];
  const selectionMade = Boolean(cards.selected_capability || cards.selected_asset);
  const hasInitialRequest = Boolean(
    thread?.initial_request?.msg_uuid ||
    Object.keys(thread?.initial_request?.request_body || {}).length,
  );
  const orderedCards = useMemo(() => (
    Object.entries(cards)
      .filter(([cardKey]) => cardKey !== 'available_exchange_cards')
      .filter(([cardKey]) => cardKey !== 'files_exchanged')
      .filter(([cardKey]) => !(cardKey === 'capabilities_assets_offers' && cards.capabilities_assets_advertisements))
      .filter(([, card]) => card)
      .sort(([, a], [, b]) => (a.order ?? 999) - (b.order ?? 999))
  ), [cards]);
  const fileTimelineMessages = useMemo(
    () => (Array.isArray(cards.files_exchanged?.files) ? cards.files_exchanged.files : []),
    [cards.files_exchanged],
  );
  const timelineItems = useMemo(() => (
    [
      ...(hasInitialRequest ? [{
        type: 'initial_request',
        key: `initial:${thread?.initial_request?.msg_uuid || thread?.initial_request?.created_at || 'request'}`,
        cardKey: 'initial_request',
        initialRequest: thread?.initial_request,
        order: 0,
        createdAt: thread?.initial_request?.created_at || '',
      }] : []),
      ...orderedCards.map(([cardKey, card]) => ({
        type: 'card',
        key: cardKey,
        cardKey,
        card,
        order: card.order ?? 999,
        createdAt: card.created_at || '',
      })),
      ...fileTimelineMessages.map((message) => ({
        type: 'file_event',
        key: `file:${message.message_uuid || message.created_at || message.body?.filename}`,
        cardKey: 'file_upload_event',
        message,
        order: 60,
        createdAt: message.created_at || '',
      })),
    ].sort(compareTimelineItems)
  ), [fileTimelineMessages, hasInitialRequest, orderedCards, thread?.initial_request]);
  const timelineItemCount = timelineItems.length;

  const selectCapability = async (capability) => {
    setSelecting(true);
    try {
      await onSelectCapability?.(capability);
    } finally {
      setSelecting(false);
    }
  };

  const selectAsset = async (asset) => {
    setSelecting(true);
    try {
      await onSelectAsset?.(asset);
    } finally {
      setSelecting(false);
    }
  };

  const pullExchangeCard = async (card, payload = null) => {
    const cardKey = card?.card_key;
    if (!cardKey) {
      return;
    }
    if (cardKey === 'xtdp' && !payload?.tdp_ids?.length && !payload?.body?.tdp_id) {
      setPendingXTdpCard(card);
      return;
    }
    setExchangeCardActionKey(`pull:${cardKey}`);
    try {
      await onPullExchangeCard?.(card, payload || {});
    } finally {
      setExchangeCardActionKey('');
    }
  };

  const attachXTdp = async (tdp, exchangeNotes) => {
    if (!pendingXTdpCard || !tdp?.tdp_id) {
      return;
    }
    await pullExchangeCard(pendingXTdpCard, buildXTdpPayload(tdp, exchangeNotes));
    setPendingXTdpCard(null);
  };

  const handleTimelineDragOver = (event) => {
    if (actionsDisabled || !onPullExchangeCard) {
      return;
    }
    const types = Array.from(event.dataTransfer?.types || []);
    if (!draggedExchangeCardKey && !types.includes(EXCHANGE_CARD_DRAG_TYPE)) {
      return;
    }
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    if (!timelineDropActive) {
      setTimelineDropActive(true);
    }
  };

  const handleTimelineDragLeave = (event) => {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setTimelineDropActive(false);
    }
  };

  const handleTimelineDrop = async (event) => {
    if (actionsDisabled || !onPullExchangeCard) {
      return;
    }
    event.preventDefault();
    const cardKey = event.dataTransfer.getData(EXCHANGE_CARD_DRAG_TYPE) || draggedExchangeCardKey;
    setTimelineDropActive(false);
    setDraggedExchangeCardKey('');
    const card = availableExchangeCardItems.find((candidate) => candidate.card_key === cardKey);
    if (card) {
      await pullExchangeCard(card);
    }
  };

  const completeExchangeCard = async (card, body = null) => {
    const cardKey = card?.card_key;
    if (!cardKey) {
      return;
    }
    setExchangeCardActionKey(`complete:${cardKey}`);
    try {
      await onCompleteExchangeCard?.(card, body);
    } finally {
      setExchangeCardActionKey('');
    }
  };

  const publishExchangeCard = async (card) => {
    const cardKey = card?.card_key;
    if (!cardKey) {
      return;
    }
    setExchangeCardActionKey(`publish:${cardKey}`);
    try {
      await onPublishExchangeCard?.(card);
    } finally {
      setExchangeCardActionKey('');
    }
  };

  const removeExchangeCard = async (card) => {
    const cardKey = card?.card_key;
    if (!cardKey) {
      return;
    }
    setExchangeCardActionKey(`remove:${cardKey}`);
    try {
      await onRemoveExchangeCard?.(card);
    } finally {
      setExchangeCardActionKey('');
    }
  };

  const saveExchangeCardEdit = async (card, payload) => {
    const cardKey = card?.card_key;
    if (!cardKey) {
      return;
    }
    setEditSaving(true);
    setExchangeCardActionKey(`edit:${cardKey}`);
    try {
      await onEditExchangeCard?.(card, payload);
      setEditingCard(null);
    } finally {
      setEditSaving(false);
      setExchangeCardActionKey('');
    }
  };

  const exportExchangeCard = async (card) => {
    const cardKey = card?.card_key;
    if (!cardKey) {
      return;
    }
    setExchangeCardActionKey(`export:${cardKey}`);
    try {
      await onExportExchangeCard?.(card);
    } finally {
      setExchangeCardActionKey('');
    }
  };

  const viewExchangeCardRevisions = async (card) => {
    const cardKey = card?.card_key;
    if (!cardKey) {
      return;
    }
    setHistoryCard(card);
    setHistoryLoading(true);
    setHistoryRevisions([]);
    try {
      const revisions = await onViewExchangeCardRevisions?.(card);
      setHistoryRevisions(Array.isArray(revisions) ? revisions : []);
    } finally {
      setHistoryLoading(false);
    }
  };

  const preparePrintJob = async (card, body = {}) => {
    const cardKey = card?.card_key;
    if (!cardKey) {
      return;
    }
    setPrintActionKey(`prepare:${cardKey}`);
    try {
      await onPreparePrintJob?.(card, body);
    } finally {
      setPrintActionKey('');
    }
  };

  const startPrintJob = async (card, printJob) => {
    const printJobId = printJob?.print_job_id;
    if (!printJobId) {
      return;
    }
    setPrintActionKey(`start:${printJobId}`);
    try {
      await onStartPrintJob?.(card, printJob);
    } finally {
      setPrintActionKey('');
    }
  };

  const confirmPrintPickup = async (card, printJob, body = {}) => {
    const printJobId = printJob?.print_job_id;
    if (!printJobId) {
      return;
    }
    setPrintActionKey(`pickup:${printJobId}`);
    try {
      await onConfirmPrintPickup?.(card, printJob, body);
    } finally {
      setPrintActionKey('');
    }
  };

  const sendEddieMessage = async (card, message) => {
    const cardKey = card?.card_key;
    if (!cardKey || !message) {
      return;
    }
    setExchangeCardActionKey(`chat:${cardKey}`);
    try {
      await onSendEddieMessage?.(card, message);
    } finally {
      setExchangeCardActionKey('');
    }
  };

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 320px' },
        gap: 2,
        alignItems: 'start',
      }}
    >
      <Box
        onDragOver={handleTimelineDragOver}
        onDragEnter={handleTimelineDragOver}
        onDragLeave={handleTimelineDragLeave}
        onDrop={handleTimelineDrop}
        sx={{
          minWidth: 0,
          position: 'relative',
          borderRadius: 2,
          border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.82)}`,
          bgcolor: (theme) => alpha(theme.palette.background.paper, 0.94),
          overflow: 'hidden',
          outline: (theme) => timelineDropActive ? `2px solid ${alpha(theme.palette.primary.main, 0.64)}` : '2px solid transparent',
          outlineOffset: 6,
          transition: 'background-color 160ms ease, outline-color 160ms ease',
        }}
      >
        {timelineDropActive ? (
          <Box
            sx={{
              position: 'absolute',
              inset: -6,
              zIndex: 3,
              borderRadius: 2,
              border: (theme) => `2px dashed ${alpha(theme.palette.primary.main, 0.8)}`,
              bgcolor: (theme) => alpha(theme.palette.background.paper, 0.72),
              pointerEvents: 'none',
              display: 'grid',
              placeItems: 'center',
              backdropFilter: 'blur(1px)',
            }}
          >
            <Chip icon={<TimelineRoundedIcon />} color="primary" label="Drop to add" sx={{ fontWeight: 900 }} />
          </Box>
        ) : null}
        <Box
          sx={{
            px: { xs: 1.5, md: 2 },
            py: 1.25,
            borderBottom: (theme) => `1px solid ${alpha(theme.palette.divider, 0.82)}`,
            bgcolor: (theme) => alpha(theme.palette.background.paper, 0.98),
            position: 'sticky',
            top: 0,
            zIndex: 2,
          }}
        >
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between">
            <Stack direction="row" spacing={1} alignItems="center">
              <TimelineRoundedIcon color="primary" fontSize="small" />
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 900, lineHeight: 1.15 }}>
                  Thread Timeline
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {timelineItemCount} timeline item{timelineItemCount === 1 ? '' : 's'}
                </Typography>
              </Box>
            </Stack>
            {filesPanel ? (
              <Button
                size="small"
                variant="outlined"
                startIcon={<UploadFileRoundedIcon />}
                onClick={() => setFilesDialogOpen(true)}
              >
                Thread Files{threadFiles.length ? ` (${threadFiles.length})` : ''}
              </Button>
            ) : null}
          </Stack>
        </Box>

        <Box
          sx={{
            maxHeight: { xs: '72vh', lg: 'calc(100vh - 220px)' },
            overflowY: 'auto',
            px: { xs: 1.25, md: 1.75 },
            pt: 2,
            pb: 0.5,
          }}
        >
          {timelineItems.map((item) => (
            <TimelineStep key={item.key} cardKey={item.cardKey}>
              {item.type === 'file_event' ? (
                <FileTimelineMessage message={item.message} />
              ) : item.type === 'initial_request' ? (
                <Box
                  sx={{ cursor: 'pointer' }}
                  onClick={() => toggleStepExpanded(item.cardKey)}
                >
                  <InitialRequest initialRequest={item.initialRequest} expanded={isStepExpanded(item.cardKey)}/>
                </Box>
              ) : (
                <Box
                  sx={{cursor: 'pointer'}}
                  onClick={() => toggleStepExpanded(item.cardKey)}
                >
                  <CardShell
                    cardKey={item.cardKey}
                    card={item.card}
                    instruction={
                      item.card.instruction ||
                      (item.cardKey === 'capabilities_assets_offers' || item.cardKey === 'capabilities_assets_advertisements'
                        ? selectionMade
                          ? 'A selection has already been made for this thread. The previous advertisements are collapsed for reference and cannot be selected again.'
                          : 'Choose one advertised capability or asset when you are ready. After a selection is made, this thread will only allow that selected provider path.'
                        : item.cardKey === 'selected_capability' || item.cardKey === 'selected_asset'
                          ? 'Your selection has been sent to the provider. Watch for a confirmation or follow-up message from that partner.'
                          : null)
                    }
                    action={
                      (item.cardKey === 'capabilities_assets_offers' || item.cardKey === 'capabilities_assets_advertisements') && selectionMade ? (
                        <Button size="small" variant="outlined" onClick={() => setShowSelectedOffers((value) => !value)}>
                          {showSelectedOffers ? 'Hide Advertisements' : 'View Advertisements'}
                        </Button>
                      ) : null
                    }
                    collapsible
                    expanded={isStepExpanded(item.cardKey)}
                  >
                    <Collapse
                      in={isStepExpanded(item.cardKey)}
                      timeout="auto"
                      onClick={(event) => event.stopPropagation()}
                    >
                      {(() => {
                        const instructionText =
                          item.card.instruction ||
                          (item.cardKey === 'capabilities_assets_offers' || item.cardKey === 'capabilities_assets_advertisements'
                            ? selectionMade
                              ? 'A selection has already been made for this thread. The previous advertisements are collapsed for reference and cannot be selected again.'
                              : 'Choose one advertised capability or asset when you are ready. After a selection is made, this thread will only allow that selected provider path.'
                            : item.cardKey === 'selected_capability' || item.cardKey === 'selected_asset'
                              ? 'Your selection has been sent to the provider. Watch for a confirmation or follow-up message from that partner.'
                              : null);

                        if (item.cardKey === 'capabilities_assets_offers' || item.cardKey === 'capabilities_assets_advertisements') {
                          return (
                            <>
                              {instructionText ? (
                                <Alert severity="info" sx={{ mb: 1.5 }}>
                                  {instructionText}
                                </Alert>
                              ) : null}
                              <Collapse in={!selectionMade || showSelectedOffers}>
                                <OffersCard
                                  card={{ ...item.card, actions_allowed: selectionMade ? false : item.card.actions_allowed }}
                                  selecting={selecting}
                                  onSelectCapability={selectCapability}
                                  onSelectAsset={selectAsset}
                                />
                              </Collapse>
                            </>
                          );
                        }

                        if (item.cardKey === 'selected_capability' || item.cardKey === 'selected_asset') {
                          return (
                            <>
                              {instructionText ? (
                                <Alert severity="info" sx={{ mb: 1.5 }}>
                                  {instructionText}
                                </Alert>
                              ) : null}
                              <SelectionCard card={item.card} />
                            </>
                          );
                        }

                        return renderSpecialCard({
                          cardKey: item.cardKey,
                          card: item.card,
                          exchangeCardActionKey,
                          printActionKey,
                          onPullExchangeCard: onPullExchangeCard && !actionsDisabled ? pullExchangeCard : null,
                          onCompleteExchangeCard: onCompleteExchangeCard && !actionsDisabled ? completeExchangeCard : null,
                          onCreateThreadFileDownloadUrl,
                          onPublishExchangeCard: onPublishExchangeCard && !actionsDisabled ? publishExchangeCard : null,
                          onRemoveExchangeCard: onRemoveExchangeCard && !actionsDisabled ? removeExchangeCard : null,
                          onShareExchangeCard: onShareExchangeCard && !actionsDisabled ? onShareExchangeCard : null,
                          onEditExchangeCard: onEditExchangeCard && !actionsDisabled ? setEditingCard : null,
                          onExportExchangeCard: onExportExchangeCard ? exportExchangeCard : null,
                          onViewExchangeCardRevisions: onViewExchangeCardRevisions ? viewExchangeCardRevisions : null,
                          onPreparePrintJob: onPreparePrintJob && !actionsDisabled ? preparePrintJob : null,
                          onStartPrintJob: onStartPrintJob && !actionsDisabled ? startPrintJob : null,
                          onConfirmPrintPickup: onConfirmPrintPickup && !actionsDisabled ? confirmPrintPickup : null,
                          onSendEddieMessage: onSendEddieMessage && !actionsDisabled ? sendEddieMessage : null,
                          onStartInitialRequest: onStartInitialRequest && !actionsDisabled ? onStartInitialRequest : null,
                          threadFiles,
                          printJobs,
                          managedAssets,
                          managedAssetsLoading,
                          actionsDisabled,
                        }) || <MessageListCard card={item.card} />;
                      })()}
                    </Collapse>
                  </CardShell>
                </Box>
              )}
            </TimelineStep>
          ))}

          {!timelineItems.length ? (
            <Box
              sx={{
                p: 2.5,
                mb: 2,
                borderRadius: 2,
                border: (theme) => `1px dashed ${alpha(theme.palette.divider, 0.9)}`,
                bgcolor: (theme) => alpha(theme.palette.background.default, 0.48),
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                No thread cards yet
              </Typography>
            </Box>
          ) : null}
        </Box>
      </Box>

      <Box sx={{ position: { lg: 'sticky' }, top: { lg: 88 } }}>
        <CardShell
          cardKey="available_exchange_cards"
          card={availableExchangeCards || { title: 'Exchange Cards' }}
        >
          {actionsDisabled ? (
            <Alert severity="info" sx={{ mb: 1.5 }}>
              Reopen this thread to add or complete Exchange Cards.
            </Alert>
          ) : null}
          <Box
            sx={{
              maxHeight: '50vh',
              overflow: 'auto',
              pr: 1,
              pt: 0.025,
              pb: 0.025,
            }}
          >
            <AvailableExchangeCardsCard
              card={availableExchangeCards || { cards: [] }}
              actionKey={exchangeCardActionKey}
              onPullExchangeCard={onPullExchangeCard && !actionsDisabled ? pullExchangeCard : null}
              onDragStart={(card) => {
                setDraggedExchangeCardKey(card.card_key);
                setTimelineDropActive(false);
              }}
              onDragEnd={() => {
                setDraggedExchangeCardKey('');
                setTimelineDropActive(false);
              }}
              disabled={actionsDisabled}
            />
          </Box>
        </CardShell>
      </Box>
      <Dialog
        open={filesDialogOpen}
        onClose={() => setFilesDialogOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Thread Files</DialogTitle>
        <DialogContent dividers>
          {filesPanel || (
            <Typography variant="body2" color="text.secondary">
              Thread files are not available for this thread.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFilesDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <ExchangeCardEditDialog
        card={editingCard}
        open={Boolean(editingCard)}
        saving={editSaving}
        onClose={() => setEditingCard(null)}
        onSave={saveExchangeCardEdit}
      />
      <ExchangeCardRevisionDialog
        card={historyCard}
        open={Boolean(historyCard)}
        loading={historyLoading}
        revisions={historyRevisions}
        onClose={() => {
          setHistoryCard(null);
          setHistoryRevisions([]);
        }}
      />
      <XTdpPullDialog
        open={Boolean(pendingXTdpCard)}
        tdps={tdpInventory}
        loading={tdpInventoryLoading}
        busy={exchangeCardActionKey === 'pull:xtdp'}
        onClose={() => {
          if (exchangeCardActionKey !== 'pull:xtdp') {
            setPendingXTdpCard(null);
          }
        }}
        onAttach={attachXTdp}
      />
    </Box>
  );
}
